/* eslint-disable @khan/frontend/require-nltext-is-translated */
import "../../stylesheets/about-package/about.less";
import "../../stylesheets/shared-package/shared.less";

import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import Link from "@khanacademy/wonder-blocks-link";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {mediaQueries, standardTransition} from "@khan/shared-styles";
import {cssUrl} from "@khan/urls";

import PageRoot from "#app-shell-package/page-root.tsx";
import * as hacks from "#shared-styles-package/hacks.ts";

import caretDownIcon from "@phosphor-icons/core/bold/caret-down-bold.svg";

type Props = Record<any, any>;
type State = {
    learnMoreOpen: any;
    learnMoreIsExpanded: boolean;
};

// We need either isMobile or isDesktop to be true at the same time. In Chrome,
// at non-standard zoom levels (e.g., 110%), [isMobile, isDesktop] =
// media.screen.breakAtWidth results in isMobile and isDesktop not being
// opposites.
export const isMobile: any = "@media screen and (max-width: 1023px)";
export const isDesktop: any = "@media screen and (min-width: 1023.5px)";

export default class PortuguesePrivacyPolicyPage extends React.Component<
    Props,
    State
> {
    state: State = {
        learnMoreOpen: new Array(12).fill(false),
        learnMoreIsExpanded: false,
    };

    // By default, non-expanded content cannot be found by browser search.
    // If a user is trying to search the page, we want to expand all
    // the content so the whole page is searchable. Here, we listen
    // for Ctrl/⌘+F and expand all content when pressed.
    componentDidMount() {
        document.body?.classList.toggle("listen-for-search");
        window.addEventListener("keydown", this.handleWindowKeyDown, false);
    }

    componentWillUnmount() {
        document.body?.classList.remove("listen-for-search");
        window.removeEventListener("keydown", this.handleWindowKeyDown, false);
    }

    handleWindowKeyDown: (e?: any) => void = (e) => {
        if (e.which === 70 && (e.ctrlKey || e.metaKey)) {
            this.expandAll(true);
        }
    };

    toggleLearnMore(index: any) {
        const newList = this.state.learnMoreOpen;
        newList[index] = !newList[index];
        this.setState({learnMoreOpen: newList});
    }

    expandAll(bool: boolean) {
        const newList = new Array(this.state.learnMoreOpen.length).fill(bool);
        this.setState({learnMoreOpen: newList, learnMoreIsExpanded: bool});
    }

    renderTOC(): React.ReactNode {
        const links: Array<{href: string; text: string}> = [
            {
                href: "#privacy-principles",
                text: "Princípios de Privacidade da Khan Academy",
            },
            {href: "#overview", text: "Visão Geral"},
            {href: "#collection-info", text: "Coleta de Informações"},
            {href: "#use-of-info", text: "Uso de Informações"},
            {href: "#sharing", text: "Divulgação de Informações"},
            {href: "#data-retention", text: "Retenção de Informações Pessoais"},
            {href: "#advertising", text: "Patrocínio e Publicidade"},
            {href: "#transparency", text: "Transparência e Suas Escolhas"},
            {href: "#settings", text: "Configurações da Sua Conta"},
            {href: "#security", text: "Segurança de Suas Informações Pessoais"},
            {href: "#third-parties", text: "Links para Terceiros"},
            {
                href: "#changes",
                text: "Alterações a esta Política de Privacidade",
            },
            {
                href: "#additional-privacy-notices",
                text: "Avisos de Privacidade Adicionais",
            },
            {href: "#schools", text: "Uso em Escolas e por Estudantes"},
            {
                href: "#childrens-privacy",
                text: "Aviso de Privacidade para Crianças",
            },
            {href: "#donor-info", text: "Informações de Doadores"},
            {href: "#paid-products", text: "Produtos pagos"},
            {href: "#applicant", text: "Informações de Candidatos"},
            {href: "#regional-info", text: "Informações Regionais"},
        ];

        return (
            <div className={css(styles.stickyTOC)}>
                <nav className={css(styles.tableOfContentsColumn)}>
                    <h2 className={css(styles.tocTitle)}>
                        <strong>Índice</strong>
                    </h2>
                    <ul className={css(styles.tableOfContentList)}>
                        {links.map((link, index) => (
                            <li key={index}>
                                <Link href={link.href}>{link.text}</Link>
                            </li>
                        ))}
                    </ul>
                </nav>
            </div>
        );
    }

    renderLearnMore(index: any, children: React.ReactNode): React.ReactNode {
        return (
            <table className={css(styles.learnMoreTable)}>
                <tbody>
                    <tr>
                        <td
                            onClick={() => this.toggleLearnMore(index)}
                            className={css(styles.learnMoreHeaderContainer)}
                        >
                            <h3 className={css(styles.learnMoreHeader)}>
                                Saiba mais
                            </h3>
                            <PhosphorIcon
                                icon={caretDownIcon}
                                color={color.offBlack}
                                size="small"
                                style={{
                                    margin: "2%",
                                    transition: `transform 0.3s`,
                                    transform: `rotate(${
                                        this.state.learnMoreOpen[0]
                                            ? "-180deg"
                                            : "0deg"
                                    })`,
                                }}
                            />
                        </td>
                    </tr>
                    {this.state.learnMoreOpen[index] && (
                        <tr className={css(styles.learnMoreTableContents)}>
                            <td className={css(styles.learnMoreTableData)}>
                                {children}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        );
    }

    // TODO: (cat) change table header elements to be more
    // accessible to screen reader
    render(): React.ReactNode {
        return (
            <PageRoot settings={{title: t`Política de Privacidade`}}>
                <div className={css(styles.wrapper)}>
                    <div className={css(styles.header)}>
                        <div className={css(styles.headerCopy)}>
                            <h1 className={css(styles.pageTitle)}>
                                Política de Privacidade da Khan Academy
                            </h1>
                            <p className={css(styles.lastUpdated)}>
                                Última atualização: 01 de novembro de 2024
                            </p>
                        </div>
                    </div>
                    <div className={css(styles.body)}>
                        <div className={css(styles.columns)}>
                            {this.renderTOC()}

                            <div className={css(styles.contentColumn)}>
                                <p className={css(styles.paragraph)}>
                                    <em>
                                        NOTA: Este documento é uma tradução do
                                        idioma inglês e foi preparado para a
                                        conveniência do leitor. A versão em
                                        inglês deste documento é a original e a
                                        única versão que vincula as partes. Em
                                        caso de conflito entre a versão em
                                        português e inglês, a versão em inglês
                                        (disponível em www.khanacademy.org)
                                        prevalecerá.
                                    </em>
                                </p>
                                <h2
                                    id="privacy-principles"
                                    className={css(styles.sectionTitle)}
                                >
                                    Princípios de Privacidade da Khan Academy
                                </h2>
                                <h3 className={css(styles.sectionSubtitle)}>
                                    Nossa Política de Privacidade é impulsionada
                                    pelo nosso compromisso com estes Princípios
                                    de Privacidade:
                                </h3>
                                <ol className={css(styles.orderedList)}>
                                    <li>
                                        Estamos profundamente comprometidos em
                                        criar um ambiente online seguro e
                                        protegido para você.
                                    </li>
                                    <li>
                                        Não vendemos suas informações pessoais a
                                        terceiros. Estabelecemos a Khan Academy
                                        como uma organização sem fins lucrativos
                                        para que nossa missão de educação e sua
                                        confiança não entrem em conflito com um
                                        motivação com fins lucrativos.
                                    </li>
                                    <li>
                                        Nos esforçamos para fornecer acesso e
                                        controle sobre as informações que você
                                        nos fornece, levando a proteção de suas
                                        informações muito a sério.
                                    </li>
                                    <li>
                                        Tomamos precauções adicionais para
                                        nossos aprendizes mais jovens, conforme
                                        descrito em nosso{" "}
                                        <Link
                                            inline={true}
                                            href="#childrens-privacy"
                                        >
                                            Aviso de Privacidade para Crianças
                                        </Link>
                                        , incluindo a restrição de contas
                                        infantis para bloquear automaticamente
                                        recursos que permitiriam a uma criança
                                        publicar ou divulgar informações
                                        pessoais.
                                    </li>
                                    <li>
                                        Não exibimos publicidade de terceiros na
                                        Khan Academy. Nossa missão é
                                        proporcionar a você uma educação de
                                        classe mundial, não vender produtos.
                                    </li>
                                </ol>
                                <h2
                                    id="overview"
                                    className={css(styles.sectionTitle)}
                                >
                                    Visão Geral
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    A Khan Academy, Inc. ("
                                    <strong>Khan Academy</strong>", "
                                    <strong>nós</strong>", "
                                    <strong>nosso</strong>" ou "
                                    <strong>nós</strong>") é uma organização sem
                                    fins lucrativos e beneficente sediada nos
                                    Estados Unidos. Como uma organização sem
                                    fins lucrativos 501(c)(3), utilizamos as
                                    informações que coletamos para proporcionar
                                    a você uma experiência melhor e cumprir
                                    nossa missão de oferecer uma educação
                                    gratuita e de classe mundial para qualquer
                                    pessoa, em qualquer lugar. Estabelecemos a
                                    nós mesmos como uma organização sem fins
                                    lucrativos para garantir que um motivo com
                                    fins lucrativos não interfira em nossa
                                    missão de fornecer um recurso educacional
                                    confiável.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Comprometemo-nos a proteger sua privacidade.
                                    Compreendemos o quão importante a
                                    privacidade é para você e estamos
                                    comprometidos em protegê-la, criando um
                                    ambiente mais seguro para aprendizes de
                                    todas as idades. Esta Política de
                                    Privacidade explica como as informações são
                                    coletadas, usadas, divulgadas e protegidas
                                    pela Khan Academy quando você utiliza (ou
                                    acessa) nosso site em www.khanacademy.org,
                                    nosso aplicativo móvel e serviço online
                                    (coletivamente, nosso "
                                    <strong>Serviço</strong>"). Nosso aplicativo
                                    móvel Khan Academy Kids é regido por uma{" "}
                                    <Link
                                        target="_blank"
                                        inline={true}
                                        href="https://www.khanacademy.org/kids/privacy-policy"
                                    >
                                        política de privacidade diferente
                                    </Link>
                                    .
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Observação: Para cada seção desta Política
                                    de Privacidade, você pode visualizar as
                                    práticas gerais de privacidade no início da
                                    seção e obter mais informações revisando
                                    "Saiba Mais" para a seção relevante.
                                </p>
                                <h3 className={css(styles.sectionSubtitle)}>
                                    Uso por Crianças e Estudantes
                                </h3>
                                <p className={css(styles.paragraph)}>
                                    Para informações específicas sobre como
                                    coletamos, usamos e processamos informações
                                    pessoais ao fornecer o Serviço a escolas,
                                    distritos escolares e professores, consulte
                                    a seção{" "}
                                    <Link inline={true} href="#schools">
                                        Uso em Escolas e por Estudantes
                                    </Link>
                                    .
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Nosso{" "}
                                    <Link
                                        inline={true}
                                        href="#childrens-privacy"
                                    >
                                        Aviso de Privacidade para Crianças
                                    </Link>{" "}
                                    descreve como coletamos, usamos e
                                    processamos informações pessoais de crianças
                                    menores de 13 anos.
                                </p>
                                <h3
                                    id="contact"
                                    className={css(styles.sectionSubtitle)}
                                >
                                    Entre em contato com a Khan Academy
                                </h3>
                                <p className={css(styles.paragraph)}>
                                    Por favor, entre em contato com a Khan
                                    Academy caso tenha perguntas ou comentários.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Por e-mail:{" "}
                                    <Link
                                        inline={true}
                                        href="mailto:privacy@khanacademy.org"
                                    >
                                        nossa equipe de Privacidade
                                    </Link>
                                    .
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Por correio: Khan Academy, PO Box 1630,
                                    Mountain View, CA 94042.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Você também pode visitar o{" "}
                                    <Link
                                        target="_blank"
                                        inline={true}
                                        href="https://khanacademy.zendesk.com/hc/"
                                    >
                                        Centro de Ajuda da Khan Academy
                                    </Link>{" "}
                                    e a página de{" "}
                                    <Link
                                        target="_blank"
                                        inline={true}
                                        href="https://khanacademy.zendesk.com/hc/categories/200175860-Common-Questions"
                                    >
                                        Perguntas Comuns e Recursos
                                    </Link>
                                    , que contém perguntas frequentes e
                                    informações úteis que podem ser de seu
                                    interesse.
                                </p>

                                <h2
                                    id="collection-info"
                                    className={css(styles.sectionTitle)}
                                >
                                    Coleta de Informações
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    Coletamos informações de diversas maneiras,
                                    dependendo do seu uso do Serviço e das
                                    configurações da sua conta:
                                </p>
                                <ul className={css(styles.list)}>
                                    <li>
                                        Coletamos informações diretamente de
                                        você, como quando cria uma conta, se
                                        comunica conosco, participa de
                                        atividades, eventos ou pesquisas, ou
                                        fornece informações durante o uso do
                                        Serviço.
                                    </li>
                                    <li>
                                        Podemos coletar informações de outras
                                        fontes, como seus pais, professores,
                                        escola ou terceiros (organizações fora
                                        da Khan Academy), como aplicativos de
                                        terceiros que você usa para se conectar
                                        ao Serviço.
                                    </li>
                                    <li>
                                        Coletamos algumas informações
                                        automaticamente, como informações sobre
                                        o seu uso do Serviço e os dispositivos
                                        que você utiliza para acessá-lo. Podemos
                                        usar cookies, web beacons e tecnologias
                                        semelhantes para coletar informações,
                                        conforme explicado em nosso{" "}
                                        <Link
                                            target="_blank"
                                            inline={true}
                                            href="/about/cookie-policy"
                                        >
                                            Aviso de Cookies
                                        </Link>
                                        .
                                    </li>
                                </ul>
                                <p className={css(styles.paragraph)}>
                                    Exemplos de informações que podemos coletar
                                    incluem:
                                </p>
                                <ul className={css(styles.list)}>
                                    <li>
                                        Informações de registro da conta (nome
                                        de usuário, data de nascimento e e-mail)
                                    </li>
                                    <li>
                                        Informações sobre seu navegador ou
                                        dispositivo e localização geral
                                    </li>
                                    <li>
                                        Informações que você escolhe incluir em
                                        seu perfil ou postar em áreas públicas
                                        do Serviço
                                    </li>
                                    <li>
                                        Informações que você pode fornecer em
                                        conexão com recursos específicos ou
                                        programas especiais
                                    </li>
                                    <li>
                                        Informações não pessoais que podem estar
                                        vinculadas às suas informações pessoais,
                                        incluindo informações sobre o seu uso do
                                        nosso Serviço
                                    </li>
                                </ul>
                                <p className={css(styles.paragraph)}>
                                    Informações adicionais sobre a coleta de
                                    dados de crianças e estudantes são
                                    fornecidas em nosso{" "}
                                    <Link
                                        inline={true}
                                        href="#childrens-privacy"
                                    >
                                        Aviso de Privacidade para Crianças
                                    </Link>{" "}
                                    e{" "}
                                    <Link inline={true} href="#schools">
                                        Uso em Escolas e por Estudantes
                                    </Link>
                                    . Mais informações sobre como a Khan Academy
                                    coleta suas informações estão disponíveis em
                                    "Saiba mais".
                                </p>
                                <button
                                    type="button"
                                    className={css(styles.expandAll)}
                                    onClick={() =>
                                        this.expandAll(
                                            !this.state.learnMoreIsExpanded,
                                        )
                                    }
                                >
                                    {this.state.learnMoreIsExpanded
                                        ? "Recolher Todas as Seções"
                                        : "Expandir Todas as Seções"}
                                </button>

                                {this.renderLearnMore(
                                    0,
                                    <>
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Informações que você nos fornece
                                            </strong>
                                        </h4>
                                        <ul className={css(styles.list)}>
                                            <li>
                                                <strong>
                                                    Informações de contato e
                                                    perfil:
                                                </strong>{" "}
                                                Quando você cria uma conta no
                                                Serviço da Khan Academy, podemos
                                                coletar informações, incluindo
                                                seu nome, endereço de e-mail e
                                                data de nascimento ou dados de
                                                idade (para fins de
                                                conformidade). Também coletamos
                                                informações de login, incluindo
                                                nome de usuário e senha. Após o
                                                registro, você pode optar por
                                                fornecer informações adicionais
                                                em seu perfil, como seu apelido
                                                e outras informações pessoais ou
                                                demográficas.
                                            </li>
                                            <li>
                                                <strong>
                                                    Suas comunicações conosco:
                                                </strong>{" "}
                                                Podemos coletar informações de
                                                você em outros momentos, como
                                                quando solicita suporte ao
                                                cliente, nos envia um e-mail,
                                                faz um comentário ou pergunta,
                                                fornece feedback, conclui uma
                                                pesquisa de usuário, participa
                                                de um programa especial ou
                                                comunica-se de outra forma com a
                                                Khan Academy (incluindo via
                                                serviços de terceiros).
                                            </li>
                                            <li>
                                                <strong>
                                                    Suas interações com nossos
                                                    Serviços:
                                                </strong>{" "}
                                                Podemos coletar informações de
                                                você quando interage com
                                                recursos dos Serviços, como
                                                nosso chatbot alimentado por
                                                Inteligência Artificial (IA), o
                                                Khanmigo.
                                            </li>
                                        </ul>
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Informações que podemos receber
                                                sobre você de outras fontes
                                            </strong>
                                        </h4>
                                        <ul className={css(styles.list)}>
                                            <li>
                                                <strong>
                                                    Informações obtidas de uma
                                                    escola ou de outros
                                                    usuários:
                                                </strong>{" "}
                                                Disponibilizamos certos recursos
                                                em nosso Serviço que permitem
                                                que outros usuários nos forneçam
                                                informações sobre você. Por
                                                exemplo, uma escola ou professor
                                                pode fornecer informações
                                                relacionadas a participantes de
                                                uma classe, ou um pai pode
                                                fornecer informações
                                                relacionadas a um Usuário
                                                Infantil, como quando um pai
                                                registra uma conta para seu
                                                filho e nos fornece determinadas
                                                informações sobre a criança
                                                (como nome de usuário e idade).
                                                Consulte nosso{" "}
                                                <Link
                                                    inline={true}
                                                    href="#childrens-privacy"
                                                >
                                                    Aviso de Privacidade para
                                                    Crianças
                                                </Link>{" "}
                                                e{" "}
                                                <Link
                                                    inline={true}
                                                    href="#schools"
                                                >
                                                    Uso em Escolas e por
                                                    Estudantes
                                                </Link>{" "}
                                                para obter informações
                                                adicionais sobre o uso do nosso
                                                Serviço por crianças e
                                                estudantes.
                                            </li>
                                            <li>
                                                <strong>
                                                    Informações de serviços de
                                                    autenticação Single Sign On
                                                    (SSO):
                                                </strong>{" "}
                                                Se decidir se registrar para uma
                                                conta (ou acessar sua conta) por
                                                meio de um serviço de
                                                autenticação (como Contas do
                                                Google ou Clever como um Logon
                                                Único, também conhecido como
                                                SSO) ("Serviço de
                                                Autenticação"), podemos coletar
                                                informações desses serviços de
                                                terceiros, de acordo com suas
                                                configurações de privacidade em
                                                sua conta no Serviço de
                                                Autenticação relevante.
                                            </li>
                                            <li>
                                                <strong>
                                                    Informações que coletamos
                                                    para permitir sua
                                                    participação em programas ou
                                                    recursos especiais:
                                                </strong>{" "}
                                                A Khan Academy pode apresentar
                                                um programa ou atividade
                                                especial oferecido em parceria
                                                com terceiros. Se você
                                                participar desses programas
                                                especiais, a Khan Academy poderá
                                                coletar informações de você para
                                                facilitar esses programas.
                                            </li>
                                            <li>
                                                <strong>
                                                    Informações fornecidas por
                                                    parceiros e outras fontes:
                                                </strong>{" "}
                                                Também podemos receber
                                                informações de organizações
                                                parceiras de terceiros com as
                                                quais colaboramos para fornecer
                                                serviços educacionais. Se
                                                associarmos informações de
                                                outras fontes com informações
                                                pessoais que coletamos por meio
                                                de nosso Serviço, trataremos as
                                                informações combinadas como
                                                informações pessoais, de acordo
                                                com esta Política de
                                                Privacidade.
                                            </li>
                                        </ul>
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Informações que coletamos
                                                automaticamente
                                            </strong>
                                        </h4>
                                        <ul className={css(styles.list)}>
                                            <li>
                                                <strong>
                                                    Informações sobre o uso do
                                                    nosso Serviço:
                                                </strong>{" "}
                                                Podemos coletar informações
                                                sobre o seu uso do nosso
                                                Serviço, como o número de
                                                problemas que você tentou
                                                resolver, o número de vídeos que
                                                assistiu, os tipos de exercícios
                                                que realizou, os temas
                                                estudados, o tempo gasto para
                                                resolver um problema e dados
                                                relacionados a conversas e
                                                atividades do Khanmigo. Isso nos
                                                permite adaptar melhor as
                                                experiências educacionais que
                                                são mais adequadas para você.
                                            </li>
                                            <li>
                                                <strong>
                                                    Informações de localização:
                                                </strong>{" "}
                                                Podemos coletar e usar
                                                informações sobre sua
                                                localização (como seu país) ou
                                                inferir sua localização
                                                aproximada com base no seu
                                                endereço IP para fornecer
                                                experiências educacionais
                                                personalizadas para sua região,
                                                mas não coletamos informação
                                                precisa de geolocalização. Você
                                                pode alterar as configurações no
                                                seu computador ou dispositivo
                                                móvel para evitar que ele nos
                                                forneça qualquer informação de
                                                localização.
                                            </li>
                                            <li>
                                                <strong>
                                                    Informações de uso e
                                                    dispositivo:{" "}
                                                </strong>
                                                Para fornecer uma experiência de
                                                aprendizado personalizada e de
                                                alta qualidade para nossos
                                                usuários, podemos usar várias
                                                tecnologias que automaticamente
                                                registram determinadas
                                                informações técnicas do seu
                                                navegador ou dispositivo quando
                                                você visita nosso site, lê
                                                nossos e-mails, usa nosso
                                                Serviço ou se envolve conosco de
                                                outra forma (por exemplo, o
                                                modelo do laptop ou dispositivo
                                                móvel e a versão do sistema
                                                operacional que você usa). Você
                                                pode obter mais informações
                                                sobre nosso uso dessas
                                                tecnologias (e como gerenciar
                                                seu uso) em nosso{" "}
                                                <Link
                                                    target="_blank"
                                                    inline={true}
                                                    href="/about/cookie-policy"
                                                >
                                                    Aviso de Cookies
                                                </Link>
                                                .
                                            </li>
                                        </ul>
                                    </>,
                                )}

                                <h2
                                    id="use-of-info"
                                    className={css(styles.sectionTitle)}
                                >
                                    Uso de Informações
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    A Khan Academy utiliza as informações
                                    coletadas para os seguintes propósitos:
                                </p>
                                <ul className={css(styles.list)}>
                                    <li>
                                        <strong>Fornecer o Serviço:</strong>{" "}
                                        Utilizamos as informações para oferecer
                                        o Serviço, permitindo que você acesse e
                                        utilize nossos recursos educacionais.
                                    </li>
                                    <li>
                                        <strong>
                                            Personalizar sua experiência:
                                        </strong>{" "}
                                        As informações coletadas nos ajudam a
                                        personalizar sua experiência no Serviço,
                                        adaptando-o de acordo com suas
                                        preferências e necessidades
                                        educacionais.
                                    </li>
                                    <li>
                                        <strong>
                                            Comunicar-se com você sobre sua
                                            conta e nosso Serviço:
                                        </strong>{" "}
                                        Utilizamos as informações para manter
                                        comunicação com você sobre sua conta,
                                        responder a perguntas e fornecer
                                        informações sobre novos recursos e
                                        ofertas que possam ser do seu interesse.
                                    </li>
                                    <li>
                                        <strong>
                                            Possibilitar sua participação em
                                            programas especiais:
                                        </strong>{" "}
                                        Se você optar por participar de
                                        programas especiais oferecidos em
                                        parceria com terceiros, usamos suas
                                        informações para facilitar sua
                                        participação nesses programas.
                                    </li>
                                    <li>
                                        <strong>
                                            Compreender e melhorar nosso
                                            Serviço:
                                        </strong>{" "}
                                        Analisamos as informações coletadas para
                                        compreender e aprimorar nosso Serviço,
                                        garantindo sua eficácia e relevância
                                        educacional. Além disso, utilizamos
                                        essas informações para o desenvolvimento
                                        de novas ofertas educacionais ou
                                        melhorias nas existentes.
                                    </li>
                                </ul>
                                <p className={css(styles.paragraph)}>
                                    Também podemos utilizar elementos de dados
                                    não pessoais (como informações de uso
                                    desidentificadas, anonimizadas ou agregadas
                                    e feedback) para desenvolvimento de
                                    produtos, pesquisa, análises e outros fins,
                                    incluindo análise, aprimoramento ou
                                    marketing do nosso Serviço, demonstração do
                                    impacto do nosso Serviço ou condução de
                                    pesquisas educacionais.
                                </p>

                                {this.renderLearnMore(
                                    1,
                                    <>
                                        <p className={css(styles.paragraph)}>
                                            <strong>
                                                Khan Academy utiliza suas
                                                informações nas seguintes
                                                formas:
                                            </strong>
                                        </p>
                                        <br />
                                        <ul className={css(styles.list)}>
                                            <li>
                                                <strong>
                                                    Para fornecer nosso Serviço:
                                                </strong>{" "}
                                                Utilizamos suas informações para
                                                operar, manter e oferecer todos
                                                os recursos de nosso Serviço.
                                            </li>
                                            <li>
                                                <strong>
                                                    Para personalizar sua
                                                    experiência:
                                                </strong>{" "}
                                                Utilizamos suas informações para
                                                personalizar sua experiência ao
                                                utilizar o Serviço, inclusive em
                                                vários dispositivos pelos quais
                                                você pode acessar o Serviço. Por
                                                exemplo, a Khan Academy lembra
                                                suas atividades recentes para
                                                recomendar o conteúdo mais
                                                apropriado em sua próxima visita
                                                e proporcionar experiências de
                                                aprendizado personalizadas.
                                                Podemos processar suas
                                                informações usando IA (como
                                                quando você utiliza o Khanmigo,
                                                nossa oferta de ferramentas e
                                                serviços impulsionados por IA).
                                            </li>
                                            <li>
                                                <strong>
                                                    Para comunicar-se com você:
                                                </strong>{" "}
                                                Utilizamos suas informações para
                                                nos comunicarmos com você sobre
                                                sua conta e responder a
                                                consultas. Também podemos usar
                                                suas informações para fornecer
                                                informações sobre os recursos,
                                                serviços e outras ofertas da
                                                Khan Academy que possam ser do
                                                seu interesse, bem como
                                                comunicar-nos sobre nossa missão
                                                filantrópica e captação de
                                                recursos para nossa missão.
                                            </li>
                                            <li>
                                                <strong>
                                                    Para possibilitar sua
                                                    participação em parcerias da
                                                    Khan Academy:
                                                </strong>{" "}
                                                Utilizamos suas informações para
                                                possibilitar sua participação em
                                                programas ou recursos que
                                                podemos oferecer em parceria com
                                                terceiros, na medida em que você
                                                decida participar desses
                                                programas.
                                            </li>
                                            <li>
                                                <strong>
                                                    Para entender, melhorar e
                                                    desenvolver nosso Serviço:
                                                </strong>{" "}
                                                Khan Academy utiliza informações
                                                para compreender e analisar as
                                                tendências de uso,
                                                comportamentos de aprendizado e
                                                preferências de nossos usuários,
                                                aprimorar a funcionalidade e
                                                aparência do Serviço, e criar
                                                recursos e funcionalidades. Para
                                                melhorar a precisão e eficácia
                                                do aprendizado de nosso Serviço,
                                                revisamos manualmente parte do
                                                Conteúdo do Usuário (conforme
                                                definido na Seção 5.1 (Conteúdo
                                                do Usuário e Propriedade) de
                                                nossos{" "}
                                                <Link
                                                    target="_blank"
                                                    inline={true}
                                                    href="/about/docs/khan-academy-terms-of-service"
                                                >
                                                    Termos de Serviço
                                                </Link>
                                                ).
                                            </li>
                                            <li>
                                                <strong>
                                                    Para desenvolver novas
                                                    ofertas educacionais:
                                                </strong>{" "}
                                                Podemos utilizar informações
                                                para manter, desenvolver, apoiar
                                                e melhorar nosso Serviço e
                                                outros produtos e serviços
                                                educacionais, além de pesquisa
                                                educacional.
                                            </li>
                                            <li>
                                                <strong>
                                                    Por razões legais, de
                                                    conformidade ou segurança:
                                                </strong>{" "}
                                                Podemos revisar, usar ou
                                                divulgar informações para
                                                cumprir leis, regras ou
                                                regulamentos aplicáveis, nossos{" "}
                                                <Link
                                                    target="_blank"
                                                    inline={true}
                                                    href="/about/docs/khan-academy-terms-of-service"
                                                >
                                                    Termos de Serviço
                                                </Link>
                                                , incluindo a proteção da
                                                segurança e direitos da Khan
                                                Academy, usuários e do público
                                                em geral. As conversas com
                                                Khanmigo estão sujeitas a
                                                revisão por motivos de segurança
                                                e conformidade.
                                            </li>
                                        </ul>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            Para informações sobre o
                                            processamento de informações
                                            pessoais relevantes para visitantes
                                            internacionais fora dos Estados
                                            Unidos, consulte{" "}
                                            <Link
                                                inline={true}
                                                href="#regional-info"
                                            >
                                                Informações Regionais
                                            </Link>
                                            .
                                        </p>
                                    </>,
                                )}

                                <h2
                                    id="sharing"
                                    className={css(styles.sectionTitle)}
                                >
                                    Divulgação de Informações
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    A Khan Academy tem o máximo cuidado para
                                    proteger as informações pessoais que você
                                    nos fornece. Não vendemos suas informações
                                    pessoais a terceiros. Esta seção explica as
                                    circunstâncias em que podemos divulgar
                                    informações pessoais a terceiros.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Podemos divulgar informações pessoais:
                                </p>
                                <ul className={css(styles.list)}>
                                    <li>
                                        Com outros usuários do nosso Serviço: Se
                                        você usar recursos que permitem
                                        compartilhar suas informações com outros
                                        usuários ou torná-las acessíveis a eles.
                                    </li>
                                    <li>
                                        Com fornecedores, consultores e outros
                                        prestadores de serviços que trabalham em
                                        nosso nome.
                                    </li>
                                    <li>
                                        Com outros usuários associados à sua
                                        conta: Isso pode incluir pais,
                                        professores ou treinadores, dependendo
                                        da natureza da sua conta.
                                    </li>
                                    <li>
                                        Com sua escola: Se você estiver usando
                                        nosso Serviço para fins escolares
                                        (consulte{" "}
                                        <Link inline={true} href="#schools">
                                            Uso em Escolas e por Estudantes
                                        </Link>{" "}
                                        para obter mais informações).
                                    </li>
                                    <li>
                                        Com seu consentimento: Isso inclui
                                        aplicativos de terceiros que você
                                        escolhe usar, como serviços
                                        complementares ou um Serviço de
                                        Autenticação. Se você optar por usar um
                                        aplicativo ou serviço de terceiros, o
                                        uso de suas informações por esse
                                        terceiro será regido pela política de
                                        privacidade desse terceiro.
                                    </li>
                                    <li>
                                        Em conexão com transferências
                                        comerciais: Em caso de fusões e
                                        aquisições.
                                    </li>
                                    <li>
                                        Por motivos de conformidade: Quando
                                        razoavelmente necessário para proteger a
                                        segurança e a integridade de nossos
                                        usuários ou Serviço, ou quando permitido
                                        por lei.
                                    </li>
                                </ul>
                                <p className={css(styles.paragraph)}>
                                    Também podemos divulgar informações
                                    desidentificadas ou agregadas que não
                                    identificam razoavelmente qualquer
                                    indivíduo.
                                </p>

                                {this.renderLearnMore(
                                    2,
                                    <>
                                        <h4 className={css(styles.paragraph)}>
                                            <strong>
                                                A Khan Academy pode divulgar ou
                                                transferir suas informações das
                                                seguintes maneiras:
                                            </strong>
                                        </h4>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Quando as informações são
                                                visíveis para outros no Serviço
                                                por padrão
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Certas características e funções do
                                            Serviço podem divulgar ou tornar
                                            suas informações acessíveis a
                                            outros. Por exemplo, suas
                                            informações podem ser visíveis para
                                            outros nas seguintes circunstâncias:
                                        </p>
                                        <ul className={css(styles.list)}>
                                            <li>
                                                <strong>
                                                    Informações que você posta
                                                    em espaços públicos do
                                                    Serviço:
                                                </strong>{" "}
                                                Quando você posta uma pergunta
                                                ou resposta no Serviço, seu
                                                perfil, que pode incluir seu
                                                nome e breve biografia, bem como
                                                o conteúdo do seu comentário
                                                postado, é visível para outros
                                                no Serviço. Como na maioria dos
                                                serviços online, uma vez que
                                                suas informações estão
                                                disponíveis online para outros,
                                                elas podem ser coletadas e
                                                utilizadas pelos destinatários
                                                ou por qualquer outro visitante
                                                do site sem restrições. Como com
                                                a segurança online em geral,
                                                considere quais informações
                                                pessoais (se houver) você deseja
                                                divulgar em seu perfil ou
                                                postagens. Recomendamos
                                                fortemente que você não use seu
                                                nome real (ou outras informações
                                                que possam ser usadas para
                                                identificá-lo) ao criar seu nome
                                                de usuário (ou perfil).
                                            </li>
                                            <li>
                                                <strong>
                                                    Informações divulgadas
                                                    quando sua conta está
                                                    conectada a outra conta de
                                                    usuário:
                                                </strong>{" "}
                                                Quando sua conta está conectada
                                                a um professor, treinador ou seu
                                                pai, essa pessoa poderá ver suas
                                                informações de perfil e sua
                                                atividade de aprendizado no
                                                Serviço, como quais vídeos você
                                                assistiu, as perguntas e
                                                respostas que postou, histórico
                                                de chat e outras informações de
                                                uso. Essas informações podem
                                                incluir seu nome (não apenas seu
                                                nome de usuário), bem como
                                                informações em seu perfil e
                                                configurações de conta.
                                            </li>
                                        </ul>
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Com seu consentimento ou de
                                                acordo com suas instruções
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Você pode fornecer consentimento ou
                                            autorização para divulgar suas
                                            informações a aplicativos ou
                                            serviços de terceiros de várias
                                            maneiras. Observe que esses
                                            terceiros não são regidos pela Khan
                                            Academy ou pelos termos desta
                                            Política de Privacidade.
                                            Recomendamos que você revise a
                                            política de privacidade de qualquer
                                            aplicativo ou serviço de terceiros
                                            antes de autorizar o acesso às
                                            informações de sua conta na Khan
                                            Academy.
                                        </p>
                                        <ul className={css(styles.list)}>
                                            <li>
                                                <strong>
                                                    Podemos divulgar informações
                                                    a aplicativos de terceiros
                                                    que você autoriza:
                                                </strong>{" "}
                                                Desenvolvedores de aplicativos
                                                de terceiros e prestadores de
                                                serviços podem criar serviços
                                                complementares para nossa
                                                plataforma, como um aplicativo
                                                móvel para alunos cegos ou com
                                                baixa visão. Eles também podem
                                                usar nosso conteúdo da Khan
                                                Academy para criar aplicativos e
                                                serviços não afiliados. Se você
                                                conectar sua conta da Khan
                                                Academy a um aplicativo ou
                                                serviço de terceiros, ou aprovar
                                                o acesso à sua conta da Khan
                                                Academy por um terceiro, você
                                                consente que a Khan Academy
                                                divulgue suas informações a esse
                                                terceiro.
                                            </li>
                                            <li>
                                                <strong>
                                                    Podemos divulgar informações
                                                    a ferramentas de
                                                    autenticação Single Sign On
                                                    (SSO) de terceiros que você
                                                    escolhe:
                                                </strong>{" "}
                                                A Khan Academy oferece
                                                oportunidades para você
                                                registrar uma conta (e/ou
                                                entrar) na Khan Academy usando
                                                um serviço de autenticação
                                                Single Sign On. Se você optar
                                                por usar o Serviço de
                                                Autenticação, esses serviços de
                                                terceiros podem coletar ou
                                                inferir informações sobre você,
                                                incluindo informações sobre o
                                                uso de nosso Serviço (ou seja,
                                                sua atividade de login), de
                                                acordo com suas próprias
                                                políticas de privacidade.
                                            </li>
                                            <li>
                                                <strong>
                                                    Podemos divulgar informações
                                                    em conexão com programas
                                                    especiais em que você
                                                    participa:
                                                </strong>{" "}
                                                Se você participar de programas
                                                especiais nos quais a Khan
                                                Academy faz parceria com
                                                terceiros, a Khan Academy pode
                                                divulgar informações coletadas
                                                de ou sobre você a seus
                                                parceiros terceirizados para
                                                facilitar o programa ou os
                                                serviços oferecidos. Antes de
                                                optar por participar de um
                                                programa especial, informaremos
                                                se e quais informações são
                                                divulgadas a terceiros que
                                                oferecem esse programa especial.
                                                Esses parceiros podem usar suas
                                                informações conforme descrito em
                                                suas próprias políticas de
                                                privacidade.
                                            </li>
                                            <li>
                                                <strong>
                                                    Podemos divulgar informações
                                                    com seu consentimento:
                                                </strong>{" "}
                                                A Khan Academy não divulga suas
                                                informações a organizações de
                                                terceiros para fins de marketing
                                                ou promoção. Em algumas
                                                instâncias, você pode conceder
                                                permissão para que divulguemos
                                                suas informações a parceiros
                                                autorizados, organizações sem
                                                fins lucrativos e outras
                                                entidades não afiliadas à Khan
                                                Academy. Nestes casos,
                                                forneceremos a esses terceiros
                                                apenas as informações que você
                                                autorizou ou nos pediu para
                                                divulgar, para o propósito
                                                específico que você autoriza e
                                                não para fins de marketing ou
                                                promocionais.
                                            </li>
                                        </ul>
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Podemos divulgar informações à
                                                sua escola
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Se você é um usuário de escola ou
                                            professor, a Khan Academy pode
                                            divulgar certas informações à sua
                                            escola. Para mais informações,
                                            consulte a seção intitulada "{" "}
                                            <Link inline={true} href="#schools">
                                                Uso em Escolas e por Estudantes
                                            </Link>
                                            ".
                                        </p>
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Podemos divulgar informações
                                                desidentificadas ou agregadas
                                                para outros:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Em determinadas circunstâncias, a
                                            Khan Academy pode divulgar
                                            informações desidentificadas ou
                                            agregadas que não o identificam
                                            razoavelmente como indivíduo, para
                                            diversos fins. Por exemplo, podemos
                                            compartilhar informações
                                            desidentificadas com parceiros
                                            comerciais para melhorar nosso
                                            Serviço ou ofertas, ou para realizar
                                            pesquisas educacionais. Se
                                            divulgarmos informações a terceiros
                                            para fins de pesquisa ou
                                            desenvolvimento de produtos, essas
                                            informações serão agregadas e/ou
                                            desidentificadas para evitar
                                            razoavelmente a identificação de
                                            indivíduos específicos.
                                        </p>
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Divulgaremos informações a
                                                funcionários e prestadores de
                                                serviços:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Para fornecer nosso Serviço, a Khan
                                            Academy pode divulgar informações a
                                            nossos funcionários e fornecedores
                                            de confiança, prestadores de
                                            serviços terceirizados ou outras
                                            pessoas, como entidades que fornecem
                                            faturamento, suporte ao cliente,
                                            análises, hospedagem, processamento
                                            de dados, marketing e serviços
                                            similares. Quando divulgamos
                                            informações a prestadores de
                                            serviços terceirizados ou
                                            fornecedores, esses terceiros são
                                            contratualmente obrigados a manter a
                                            segurança e confidencialidade dessas
                                            informações. Este{" "}
                                            <Link
                                                inline={true}
                                                target="_blank"
                                                href="https://support.khanacademy.org/hc/articles/4407295921805-What-are-the-subprocessors-that-Khan-Academy-uses-"
                                            >
                                                artigo sobre provedores de
                                                serviços terceirizados
                                            </Link>{" "}
                                            fornece mais informações sobre quem
                                            são nossos sub-processadores e quais
                                            serviços eles fornecem.
                                        </p>
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Podemos divulgar informações por
                                                motivos legais ou comerciais:
                                            </strong>
                                        </h4>
                                        <ul className={css(styles.list)}>
                                            <li>
                                                <strong>
                                                    Podemos divulgar informações
                                                    no contexto de uma mudança
                                                    nos negócios, incluindo uma
                                                    fusão ou aquisição.
                                                </strong>{" "}
                                                Se a Khan Academy estiver
                                                envolvida em uma fusão,
                                                aquisição, falência, mudança de
                                                controle ou qualquer forma de
                                                venda de parte ou totalidade de
                                                nossos ativos, suas informações
                                                podem ser transferidas ou
                                                divulgadas em conexão com essa
                                                transação comercial. Se a
                                                transação envolver a
                                                transferência de Dados Pessoais
                                                do Estudante a um terceiro,
                                                exigiremos que o novo
                                                proprietário continue a
                                                respeitar os termos fornecidos
                                                nesta Política de Privacidade,
                                                ou forneceremos à Escola um
                                                aviso e a oportunidade de optar
                                                por não participar da
                                                transferência de Dados Pessoais
                                                do Estudante excluindo esses
                                                dados antes que a transferência
                                                ocorra.
                                            </li>
                                            <li>
                                                <strong>
                                                    Em outras instâncias:
                                                </strong>{" "}
                                                A Khan Academy pode divulgar
                                                informações se tivermos uma
                                                crença de boa fé de que fazê-lo
                                                é razoavelmente necessário para:
                                                (a) cumprir qualquer lei,
                                                regulamento, processo legal ou
                                                solicitação governamental
                                                exigível; (b) fazer cumprir os{" "}
                                                <Link
                                                    target="_blank"
                                                    inline={true}
                                                    href="/about/docs/khan-academy-terms-of-service"
                                                >
                                                    Termos de Serviço
                                                </Link>{" "}
                                                aplicáveis, incluindo a
                                                investigação de violações
                                                potenciais; (c) investigar ou
                                                nos defender contra quaisquer
                                                reivindicações ou alegações de
                                                terceiros; (d) detectar,
                                                prevenir ou abordar fraudes,
                                                questões de segurança ou
                                                técnicas; (e) proteger os
                                                direitos, propriedade ou
                                                segurança pessoal da Khan
                                                Academy (e sua equipe), nossos
                                                usuários ou o público; ou (f)
                                                conforme exigido ou permitido
                                                por lei.
                                            </li>
                                        </ul>
                                    </>,
                                )}
                                <h2
                                    id="data-retention"
                                    className={css(styles.sectionTitle)}
                                >
                                    Retenção de Informações Pessoais
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    Nossa prática é reter seus dados pessoais
                                    apenas pelo tempo necessário para fornecer
                                    nossos serviços a você, incluindo a
                                    manutenção da sua conta na Khan Academy. Ao
                                    determinar por quanto tempo retemos seus
                                    dados pessoais, consideramos como a retenção
                                    das informações ajudará o aprendiz (ou a
                                    escola, no caso de contas escolares), nossos
                                    requisitos legais e outras necessidades
                                    comerciais. Nossos períodos de retenção
                                    variam dependendo do tipo de dado envolvido,
                                    mas, geralmente, consideramos os seguintes
                                    fatores ao determinar por quanto tempo
                                    retemos dados pessoais:
                                </p>
                                <ul className={css(styles.list)}>
                                    <li>
                                        Se os dados são necessários para manter
                                        uma conta na Khan Academy
                                    </li>
                                    <li>
                                        Se os dados são necessários para apoiar
                                        um propósito educacional
                                    </li>
                                    <li>
                                        Se temos uma exigência contratual para
                                        reter os dados
                                    </li>
                                    <li>
                                        Se temos uma necessidade de reter dados
                                        para fins comerciais legítimos, como
                                        para manter registros ou prevenir
                                        fraudes ou abusos
                                    </li>
                                    <li>
                                        Se os dados são necessários para
                                        processar compras e resolver questões
                                        relacionadas a pagamentos, assinaturas
                                        ou doações
                                    </li>
                                    <li>
                                        Se nossos usuários têm a capacidade de
                                        acessar e excluir seus dados, e
                                    </li>
                                    <li>
                                        Se nossos usuários razoavelmente
                                        esperariam que reteríamos os dados até
                                        que a conta na Khan Academy fosse
                                        encerrada ou cancelada, ou até que
                                        solicitem a exclusão.
                                    </li>
                                </ul>
                                <p className={css(styles.paragraph)}>
                                    Quando não tivermos uma necessidade
                                    comercial legítima contínua para processar
                                    suas informações pessoais, nossa política é
                                    excluir ou desidentificar os dados. Podemos
                                    reter e usar dados desidentificados ou
                                    anonimizados por períodos mais longos para
                                    finalidades como desenvolvimento de
                                    produtos, pesquisa, análise e para
                                    demonstrar o impacto do nosso serviço.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Informações adicionais sobre nossas práticas
                                    de exclusão podem ser encontradas em nosso
                                    Artigo no Centro de Ajuda:{" "}
                                    <Link
                                        target="_blank"
                                        inline={true}
                                        href="https://support.khanacademy.org/hc/articles/29415446635021-What-are-Khan-Academy-s-Deletion-Practices"
                                    >
                                        "Quais são as práticas de exclusão da
                                        Khan Academy?"
                                    </Link>
                                </p>
                                <h2
                                    id="advertising"
                                    className={css(styles.sectionTitle)}
                                >
                                    Patrocínio e Publicidade
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    Como uma organização sem fins lucrativos, a
                                    Khan Academy depende de nossos
                                    patrocinadores, doadores e outros
                                    colaboradores para fornecer o financiamento
                                    necessário para oferecer o Serviço gratuito
                                    aos nossos usuários. De tempos em tempos,
                                    permitimos que terceiros patrocinem conteúdo
                                    exibido em nosso Serviço.
                                </p>
                                <ul className={css(styles.list)}>
                                    <li>
                                        Por exemplo: Organizações com fins
                                        lucrativos podem desejar patrocinar todo
                                        o conteúdo relacionado a um tópico
                                        educacional específico, como astronomia
                                        ou biologia.
                                    </li>
                                    <li>
                                        O conteúdo patrocinado sempre será
                                        rotulado: (por exemplo, "Patrocinado por
                                        ___").
                                    </li>
                                    <li>
                                        A Khan Academy não divulga suas
                                        informações pessoais para esses
                                        patrocinadores sem seu consentimento.
                                        Não fornecemos a esses patrocinadores a
                                        capacidade de rastrear ou coletar
                                        informações sobre os visitantes ou
                                        usuários do nosso site.
                                    </li>
                                </ul>
                                <p className={css(styles.paragraph)}>
                                    A Khan Academy não exibe anúncios de
                                    terceiros em nosso Serviço. Podemos, de
                                    tempos em tempos, incorporar conteúdo ou
                                    vincular a conteúdo fornecido por terceiros
                                    que possa ser de interesse para você e
                                    relevante para o contexto educacional de
                                    nosso Serviço. Alguns desses materiais ou
                                    sites podem incluir branding ou anúncios,
                                    conforme permitido pelo proprietário ou
                                    operador de terceiros. Para obter mais
                                    informações, consulte{" "}
                                    <Link inline={true} href="#third-parties">
                                        Links para terceiros
                                    </Link>
                                    .
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Tomamos medidas para evitar a coleta de
                                    informações para fins de publicidade
                                    direcionada quando acreditamos que o Serviço
                                    está sendo usado por Estudantes ou Crianças.
                                </p>
                                <ul className={css(styles.list)}>
                                    <li>
                                        Por exemplo, evitamos o uso de
                                        tecnologia de rastreamento em páginas da
                                        web com conteúdo direcionado a crianças,
                                        e não utilizamos tecnologia de
                                        rastreamento para publicidade de
                                        terceiros.
                                    </li>
                                    <li>
                                        A Khan Academy utiliza a funcionalidade
                                        de privacidade no YouTube para exibir
                                        conteúdo de vídeo no YouTube (consulte
                                        informações adicionais em "Saiba mais").
                                    </li>
                                </ul>
                                <p className={css(styles.paragraph)}>
                                    Consulte nosso{" "}
                                    <Link
                                        inline={true}
                                        href="#childrens-privacy"
                                    >
                                        Aviso de Privacidade para Crianças
                                    </Link>{" "}
                                    para obter mais informações.
                                </p>

                                {this.renderLearnMore(
                                    3,
                                    <>
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>YouTube</strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Habilitamos o "Privacy-Enhanced
                                            Mode" para vídeos do YouTube
                                            exibidos no Serviço. Isso significa
                                            que o YouTube não coloca cookies nem
                                            rastreia o comportamento de
                                            visualização para fins
                                            publicitários. Nosso conteúdo
                                            voltado para estudantes não
                                            incorpora nenhum vídeo do YouTube
                                            que não faça parte do nosso
                                            conteúdo. Desativamos certas
                                            funcionalidades no YouTube (chamadas
                                            de funcionalidade "rel"), para que o
                                            conteúdo fora do nosso conteúdo não
                                            seja reproduzido. Também usamos as
                                            tags de "tratamento direcionado a
                                            crianças" do Google para alertar o
                                            Google de que nossos vídeos do
                                            YouTube podem ser visualizados por
                                            crianças ou estudantes. Para obter
                                            mais informações, consulte as{" "}
                                            <Link
                                                target="_blank"
                                                inline={true}
                                                href="http://www.google.com/policies/privacy/partners"
                                            >
                                                práticas de privacidade do
                                                Google
                                            </Link>
                                            .
                                        </p>
                                    </>,
                                )}

                                <h2
                                    id="transparency"
                                    className={css(styles.sectionTitle)}
                                >
                                    Transparência e Suas Escolhas
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    Levamos a privacidade muito a sério. Nos
                                    esforçamos para colocar você no controle das
                                    escolhas e decisões relacionadas às suas
                                    informações pessoais.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Entendemos que suas informações pessoais são
                                    importantes para você, e é por isso que você
                                    tem escolhas sobre como suas informações
                                    pessoais são usadas e divulgadas. Queremos
                                    que você tenha acesso às suas informações
                                    pessoais para que possa ajudar a mantê-las o
                                    mais precisas possível.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Por exemplo, você pode escolher criar uma
                                    conta (ou usar o Serviço sem se registrar).
                                    Se você se registrar para uma conta, pode:
                                </p>
                                <ul className={css(styles.list)}>
                                    <li>
                                        Limitar as informações opcionais que
                                        você fornece.
                                    </li>
                                    <li>
                                        Escolher se deseja divulgar informações
                                        pessoais a serviços de terceiros (e
                                        usá-los).
                                    </li>
                                    <li>
                                        Escolher se deseja adicionar um coach à
                                        sua conta.
                                    </li>
                                    <li>
                                        Escolher se deseja receber e-mails
                                        opcionais.
                                    </li>
                                    <li>
                                        Atualizar, corrigir e excluir suas
                                        informações de conta por meio das
                                        configurações da sua conta.
                                    </li>
                                </ul>
                                <p className={css(styles.paragraph)}>
                                    Incentivamos você a verificar regularmente
                                    as{" "}
                                    <Link
                                        inline={true}
                                        target="_blank"
                                        href="/settings/email"
                                    >
                                        configurações da sua conta
                                    </Link>
                                    .
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Em alguns casos, os controles
                                    administrativos para contas usadas em um
                                    ambiente escolar, incluindo a capacidade de
                                    modificar ou excluir a conta, são mantidos
                                    pela escola. Para obter mais informações,
                                    consulte nosso{" "}
                                    <Link
                                        inline={true}
                                        href="#childrens-privacy"
                                    >
                                        Aviso de Privacidade para Crianças
                                    </Link>{" "}
                                    e{" "}
                                    <Link inline={true} href="#schools">
                                        Uso em Escolas e por Estudantes
                                    </Link>
                                    .
                                </p>

                                {this.renderLearnMore(
                                    4,
                                    <>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Você pode usar o Serviço sem
                                                criar uma conta:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Você pode acessar a maioria de
                                            nossos recursos online sem fornecer
                                            informações pessoais. No entanto, se
                                            você não criar uma conta, a Khan
                                            Academy não poderá fornecer
                                            determinados recursos e
                                            funcionalidades encontrados em nosso
                                            Serviço. Você ainda pode visualizar
                                            conteúdo e concluir exercícios sem
                                            uma conta, mas não terá recursos
                                            completos da conta (como participar
                                            de tarefas de classe e acompanhar
                                            sua atividade de aprendizado). Você
                                            pode posteriormente ativar ou
                                            acessar esses recursos fornecendo à
                                            Khan Academy as informações
                                            necessárias para criar uma conta.
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Você pode limitar as informações
                                                pessoais que fornece:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Você pode escolher fornecer
                                            informações que não o identifiquem
                                            razoavelmente para os outros. Por
                                            exemplo, recomendamos que você
                                            selecione um nome de usuário que não
                                            esteja relacionado ao seu nome real
                                            (ou que não possa ser usado para
                                            identificá-lo). Você também pode
                                            optar por não fornecer nenhuma
                                            informação opcional em seu perfil
                                            público.
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Fóruns comunitários são
                                                opcionais:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Você escolhe se (e como) participar
                                            de fóruns acessíveis ao público,
                                            como postar em espaços públicos do
                                            Serviço. Quando você posta uma
                                            pergunta (ou resposta) no Serviço
                                            (ou participa de um fórum público),
                                            seu perfil (bem como o conteúdo do
                                            seu comentário postado) é visível
                                            para outros no Serviço. Nosso
                                            Serviço oferece blogs ou fóruns
                                            comunitários acessíveis ao público.
                                            Esteja ciente de que qualquer
                                            informação fornecida nessas áreas
                                            pode ser lida, coletada e usada por
                                            outras pessoas. Usuários Infantis
                                            não podem postar em fóruns
                                            comunitários (conforme descrito no{" "}
                                            <Link
                                                inline={true}
                                                href="#childrens-privacy"
                                            >
                                                Aviso de Privacidade para
                                                Crianças
                                            </Link>
                                            ).
                                        </p>
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Você pode desconectar (ou optar
                                                por não usar) aplicativos de
                                                terceiros:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Você escolhe quais aplicativos de
                                            terceiros deseja usar e pode parar
                                            de usar esses aplicativos a qualquer
                                            momento. Essa funcionalidade é
                                            opcional e inteiramente sua escolha.
                                            Por exemplo, você pode revogar o
                                            acesso da Khan Academy à sua conta
                                            em qualquer serviço de logon único a
                                            qualquer momento desconectando a
                                            "Conta vinculada" em suas{" "}
                                            <Link
                                                inline={true}
                                                target="_blank"
                                                href="/settings/account"
                                            >
                                                Configurações de Conta
                                            </Link>{" "}
                                            ou atualizando as configurações
                                            apropriadas nas preferências de
                                            conta do Serviço de Autenticação.
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Você pode cancelar a inscrição
                                                em e-mails opcionais:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            A Khan Academy pode, de tempos em
                                            tempos, enviar e-mails sobre nossos
                                            produtos e Serviço, ou produtos e
                                            serviços que achamos que você pode
                                            gostar. Você pode optar por não
                                            receber esses e-mails clicando no
                                            link de cancelamento de inscrição em
                                            qualquer e-mail ou atualizando suas{" "}
                                            <Link
                                                target="_blank"
                                                inline={true}
                                                href="/settings/email"
                                            >
                                                Configurações de E-mail
                                            </Link>
                                            . Observe que você não tem permissão
                                            para cancelar a inscrição ou optar
                                            por não receber certos tipos de
                                            mensagens relacionadas à sua conta,
                                            como verificação de conta,
                                            alterações ou atualizações de
                                            recursos do Serviço, avisos técnicos
                                            ou de segurança e determinadas
                                            comunicações programáticas (como
                                            tarefas de classe).
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Você pode adicionar um coach,
                                                professor ou conta escolar à sua
                                                conta:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Você pode optar por adicionar um
                                            coach ou professor à sua conta para
                                            que eles possam visualizar o perfil
                                            da sua conta e sua atividade de
                                            aprendizado no Serviço, como quais
                                            vídeos você assistiu, as perguntas e
                                            respostas que você postou e outras
                                            informações de uso. Quando sua conta
                                            está conectada a um coach, professor
                                            ou Escola no Serviço, seu coach,
                                            professor ou Escola poderá ver suas
                                            informações de perfil e sua
                                            atividade de aprendizado no Serviço.
                                            Se você vincular sua conta pessoal a
                                            uma Conta Escolar, um professor ou
                                            Escola conectada verá sua atividade
                                            de aprendizado pessoal, bem como a
                                            atividade de aprendizado da Escola.
                                            Um coach ou professor conectado
                                            também pode ver seu nome (não apenas
                                            seu nome de usuário), assim como seu
                                            endereço de e-mail, se você incluir
                                            essas informações em seu perfil da
                                            conta. Observe que algumas
                                            informações de perfil da conta e
                                            conexões de professores para
                                            determinadas Contas Escolares são
                                            controladas pela Escola.
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Você pode aprender mais sobre
                                                cookies e como gerenciar
                                                configurações de cookies em
                                                nosso Aviso de Cookies:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Para proporcionar uma experiência de
                                            aprendizado personalizada e de alta
                                            qualidade para nossos usuários,
                                            podemos usar cookies e tecnologias
                                            semelhantes que coletam
                                            automaticamente certas informações
                                            do seu navegador ou dispositivo
                                            quando você visita nosso site, lê
                                            nossos e-mails, utiliza nosso
                                            Serviço ou interage conosco de outra
                                            forma. Você pode gerenciar os
                                            cookies [funcionais] em nosso Centro
                                            de Preferências de Cookies,
                                            incluindo a possibilidade de
                                            desativar diretamente certos cookies
                                            de terceiros. Observe que escolher
                                            remover ou rejeitar certos cookies
                                            pode limitar o uso do Serviço. Para
                                            obter mais informações sobre nosso
                                            uso de cookies e outras tecnologias
                                            semelhantes, consulte nosso{" "}
                                            <Link
                                                target="_blank"
                                                inline={true}
                                                href="/about/cookie-policy"
                                            >
                                                Aviso de Cookies
                                            </Link>
                                            .
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Você pode compartilhar
                                                informações sobre cursos que
                                                está estudando por meio de
                                                recursos de compartilhamento:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Oferecemos recursos de
                                            compartilhamento claramente
                                            marcados, que permitem que você
                                            compartilhe informações sobre cursos
                                            ou tópicos que está aprendendo por
                                            meio do Serviço. O uso desses
                                            recursos de compartilhamento é
                                            inteiramente opcional.
                                        </p>
                                    </>,
                                )}

                                <h2
                                    id="settings"
                                    className={css(styles.sectionTitle)}
                                >
                                    Configurações da sua conta
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    Queremos que você tenha acesso às suas
                                    informações, para que possa ajudar a
                                    mantê-las o mais precisas possível. Se você
                                    se registrar e fornecer informações à Khan
                                    Academy, poderá atualizar, corrigir ou
                                    excluir sua conta e informações a qualquer
                                    momento, revisando suas informações de
                                    perfil e preferências na página de
                                    configurações da sua conta.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Pais (não Crianças) podem modificar ou
                                    excluir contas de Usuários Infantis,
                                    conforme descrito em nosso{" "}
                                    <Link
                                        inline={true}
                                        href="#childrens-privacy"
                                    >
                                        Aviso de Privacidade para Crianças
                                    </Link>
                                    . Pais ou usuários da escola que solicitarem
                                    modificar ou excluir Contas Escolares podem
                                    ser direcionados para a Escola.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Mais informações sobre como você pode
                                    acessar, atualizar, corrigir ou excluir sua
                                    conta nas configurações da conta estão
                                    disponíveis em "Saiba mais".
                                </p>

                                {this.renderLearnMore(
                                    5,
                                    <>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Acessar, atualizar e corrigir
                                                suas informações:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Você pode acessar, atualizar e
                                            corrigir suas informações pessoais a
                                            qualquer momento, fazendo login em
                                            sua conta e atualizando as
                                            informações relevantes na página de
                                            configurações da sua conta.
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Excluir sua conta e informações:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Para solicitar a exclusão de suas
                                            informações a qualquer momento, faça
                                            login em sua conta e selecione
                                            "excluir sua conta" em
                                            Configurações. Você pode aprender
                                            mais em nosso{" "}
                                            <Link
                                                target="_blank"
                                                inline={true}
                                                href="https://khanacademy.zendesk.com/hc/articles/202263014-How-can-I-cancel-or-delete-my-account-"
                                            >
                                                Centro de Ajuda
                                            </Link>
                                            .
                                        </p>
                                        <ul className={css(styles.list)}>
                                            <li>
                                                Observe que suas informações
                                                serão retidas em backup por até
                                                uma semana. Se você fizer login
                                                em sua conta dentro de 48 horas,
                                                sua conta não será excluída, e
                                                você precisará passar pelo
                                                processo de exclusão novamente.
                                            </li>
                                            <li>
                                                Podemos não ser capazes de
                                                excluir informações em todas as
                                                instâncias. Por exemplo, podemos
                                                reter certas informações
                                                conforme necessário para evitar
                                                fraudes ou abusos futuros, para
                                                registro ou outros fins
                                                legítimos de negócios, ou se
                                                exigido por lei. As informações
                                                pessoais retidas permanecerão
                                                sujeitas aos termos desta
                                                Política de Privacidade.
                                            </li>
                                            <li>
                                                A menos que recebamos um pedido
                                                de exclusão, reteremos suas
                                                informações enquanto sua conta
                                                estiver ativa ou pelo tempo
                                                razoavelmente necessário para
                                                apoiar os fins para os quais foi
                                                coletada. Também podemos reter
                                                informações que foram
                                                desidentificadas ou agregadas de
                                                forma que não possam mais
                                                identificar razoavelmente um
                                                indivíduo específico.
                                            </li>
                                            <li>
                                                Pais podem modificar ou excluir
                                                contas de Usuários Infantis
                                                conectadas à conta dos pais,
                                                exceto para certas Contas
                                                Escolares.
                                            </li>
                                        </ul>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>Contas Escolares:</strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            As configurações padrão para contas
                                            usadas por ou sob a direção de uma
                                            Escola podem diferir dependendo da
                                            maneira como a Khan Academy é usada
                                            por uma Escola e dos termos do
                                            acordo entre a Escola e a Khan
                                            Academy. Em alguns casos, controles
                                            administrativos, incluindo a
                                            capacidade de modificar ou excluir a
                                            conta, são de responsabilidade da
                                            Escola. Se você tiver uma Conta
                                            Escolar, entre em contato com o
                                            administrador da sua Escola para
                                            obter informações sobre sua conta.
                                            Para mais informações, consulte{" "}
                                            <Link inline={true} href="#schools">
                                                Uso em Escolas e por Estudantes
                                            </Link>
                                            .
                                        </p>
                                    </>,
                                )}

                                <h2
                                    id="security"
                                    className={css(styles.sectionTitle)}
                                >
                                    Segurança de suas informações pessoais:
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    A Khan Academy está comprometida em garantir
                                    a segurança de suas informações pessoais.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Algumas das maneiras pelas quais protegemos
                                    suas informações pessoais incluem:
                                </p>
                                <ul className={css(styles.list)}>
                                    <li>
                                        Criptografamos suas informações pessoais
                                        quando estão armazenadas em repouso.
                                    </li>
                                    <li>
                                        Protegemos suas informações pessoais com
                                        criptografia durante a transmissão pela
                                        Internet pública.
                                    </li>
                                    <li>
                                        Utilizamos salvaguardas organizacionais
                                        e técnicas razoáveis projetadas para
                                        ajudar a proteger a privacidade e
                                        segurança de suas informações pessoais.
                                    </li>
                                </ul>
                                <p className={css(styles.paragraph)}>
                                    Algumas das maneiras pelas quais
                                    incentivamos você a proteger suas
                                    informações pessoais incluem:
                                </p>
                                <ul className={css(styles.list)}>
                                    <li>
                                        Incentivamos você a criar um nome de
                                        usuário que não revele sua identidade.
                                    </li>
                                    <li>
                                        Incentivamos você a criar (e manter) uma
                                        senha forte.
                                    </li>
                                    <li>
                                        Incentivamos você a ser cuidadoso sobre
                                        o que posta e a continuar aprendendo
                                        sobre segurança online.
                                    </li>
                                </ul>
                                <p className={css(styles.paragraph)}>
                                    Utilizamos salvaguardas razoáveis para
                                    proteger nosso Serviço e suas informações
                                    pessoais, mas nenhumas medidas de segurança
                                    são perfeitas.
                                </p>

                                {this.renderLearnMore(
                                    6,
                                    <>
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                A segurança é importante para
                                                você e para a Khan Academy:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Para proteger sua privacidade e
                                            segurança, tomamos medidas razoáveis
                                            para verificar sua identidade antes
                                            de conceder acesso à conta ou fazer
                                            correções em suas informações. Por
                                            exemplo, podemos pedir que você
                                            forneça determinadas informações
                                            para confirmar sua identidade, e
                                            podemos exigir que você crie e use
                                            uma senha para acessar certas partes
                                            de nosso Serviço. Recomendamos
                                            fortemente que você crie e mantenha
                                            uma senha forte para garantir a
                                            segurança de sua conta.
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Utilizamos salvaguardas
                                                razoáveis para proteger nosso
                                                Serviço e suas informações
                                                pessoais, mas nenhuma medida de
                                                segurança é perfeita:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            A Khan Academy utiliza salvaguardas
                                            físicas, administrativas e técnicas
                                            razoáveis projetadas para preservar
                                            a integridade e segurança de suas
                                            informações pessoais que mantemos em
                                            conexão com nosso Serviço. No
                                            entanto, não podemos garantir a
                                            segurança de todas as informações
                                            que você transmite para a Khan
                                            Academy. Por exemplo, nenhum serviço
                                            da Internet ou mensagem de e-mail é
                                            totalmente seguro ou isento de
                                            erros. Em particular, e-mails
                                            enviados para ou a partir do Serviço
                                            podem não ser seguros. Por favor,
                                            tenha cuidado ao decidir quais
                                            informações enviar para nós por
                                            e-mail ou para obter ajuda no
                                            suporte. Uma vez que recebemos sua
                                            transmissão de informações, a Khan
                                            Academy faz esforços comercialmente
                                            razoáveis para garantir a segurança
                                            de nossos sistemas. Ao inserir
                                            informações sensíveis,
                                            criptografamos a transmissão dessas
                                            informações usando tecnologia de
                                            camada de soquete segura (SSL) ou
                                            tecnologias similares. No entanto,
                                            observe que isso não garante que
                                            tais informações não possam ser
                                            acessadas, divulgadas, alteradas ou
                                            destruídas por violação de qualquer
                                            uma de nossas salvaguardas físicas,
                                            técnicas ou administrativas. No caso
                                            de qualquer informação sob nosso
                                            controle ser comprometida como
                                            resultado de uma violação de
                                            segurança, tomaremos medidas
                                            razoáveis para investigar a situação
                                            e notificaremos você do incidente de
                                            segurança de acordo com as leis e
                                            regulamentos aplicáveis.
                                        </p>
                                    </>,
                                )}

                                <h2
                                    id="third-parties"
                                    className={css(styles.sectionTitle)}
                                >
                                    Links para Terceiros:
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    O Serviço pode conter links para websites
                                    operados por outras entidades ou indivíduos,
                                    e pode ser vinculado por esses websites. Se
                                    incluirmos links para terceiros e você
                                    clicar nesse link, você sairá da Khan
                                    Academy, e a política de privacidade desse
                                    terceiro se aplicará. Da mesma forma, se
                                    você encontrar um link para a Khan Academy
                                    em um website de terceiros, a política de
                                    privacidade desse terceiro se aplicará.
                                </p>
                                <ul className={css(styles.list)}>
                                    <li>
                                        Por exemplo:
                                        <ul className={css(styles.subList)}>
                                            <li>
                                                Se você clicar em um link para
                                                um programa educacional
                                                administrado por outra empresa,
                                                será redirecionado para o site
                                                dessa empresa e a política de
                                                privacidade dela se aplicará.
                                            </li>
                                            <li>
                                                Você pode optar por compartilhar
                                                informações sobre o seu uso da
                                                Khan Academy nas redes sociais.
                                                Se escolher fazer isso, a rede
                                                social poderá usar suas
                                                informações conforme descrito em
                                                suas próprias políticas de
                                                privacidade.
                                            </li>
                                            <li>
                                                Se você estiver na página do
                                                Instagram da Khan Academy, a
                                                política de privacidade do
                                                Instagram se aplicará a essa
                                                página da Khan Academy.
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        Esta Política de Privacidade da Khan
                                        Academy não se aplica (e não podemos
                                        controlar as atividades de) a esses
                                        outros websites de terceiros.
                                    </li>
                                    <li>
                                        Você deve consultar as políticas de
                                        privacidade desses websites de
                                        terceiros.
                                    </li>
                                </ul>
                                <h2
                                    id="changes"
                                    className={css(styles.sectionTitle)}
                                >
                                    Alterações a esta Política de Privacidade:
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    A Khan Academy pode modificar ou revisar
                                    esta Política de Privacidade de tempos em
                                    tempos. A Khan Academy notificará os
                                    usuários de quaisquer alterações em nossa
                                    Política de Privacidade, publicando a
                                    Política de Privacidade revisada com uma
                                    data de revisão atualizada em nosso Serviço.
                                    Se alterarmos esta Política de maneira
                                    material, faremos o possível para
                                    notificá-lo das alterações publicando um
                                    aviso em nosso site antes que essas
                                    alterações entrem em vigor. Recomendamos que
                                    você revise a Política de Privacidade cada
                                    vez que visitar o Serviço para ficar
                                    informado sobre nossas práticas de
                                    privacidade.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Não faremos alterações materiais em nossa
                                    Política de Privacidade que se relacionem
                                    com a coleta ou uso de Dados Pessoais de
                                    Estudantes sem primeiro notificar a Escola e
                                    fornecer uma escolha antes que os Dados
                                    Pessoais de Estudantes sejam usados de
                                    maneira materialmente diferente do que foi
                                    divulgado quando as informações foram
                                    coletadas.
                                </p>
                                <h2
                                    id="schools"
                                    className={css(styles.sectionTitle)}
                                >
                                    Escolas e Uso por Estudantes:
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    Esta notificação de Uso por Escolas e
                                    Estudantes complementa nossa Política de
                                    Privacidade em relação ao uso escolar. A
                                    Khan Academy é uma organização sem fins
                                    lucrativos dedicada a fornecer conteúdo
                                    educacional gratuito. Nos esforçamos para
                                    implementar as melhores práticas para
                                    proteger a privacidade de todos os nossos
                                    usuários, sejam estudantes ou não.
                                    Implementamos controles e procedimentos
                                    adicionais para escolas, distritos escolares
                                    e professores (coletivamente referidos como
                                    "Escolas") ao usarem o Serviço para fins
                                    educacionais. Quando o Serviço é usado como
                                    parte do programa educacional da Escola, as
                                    informações pessoais relacionadas aos
                                    usuários estudantis da Escola ("Usuários
                                    Escolares") podem incluir informações
                                    definidas como "registros educacionais" pela
                                    Family Educational Rights and Privacy Act ("
                                    <strong>FERPA</strong>") ou outras
                                    informações protegidas por leis estaduais
                                    similares de privacidade de dados de
                                    estudantes. Nos referimos a essas
                                    informações como "
                                    <strong>Dados Pessoais do Estudante</strong>
                                    ".
                                </p>
                                <p className={css(styles.paragraph)}>
                                    <strong>Nosso compromisso:</strong> Somos
                                    orgulhosos apoiadores do{" "}
                                    <Link
                                        target="_blank"
                                        inline={true}
                                        href="https://studentprivacypledge.org/"
                                    >
                                        Student Privacy Pledge
                                    </Link>
                                    . Nossa coleta e uso de Dados Pessoais do
                                    Estudante são regidos por nossos contratos
                                    com as Escolas, por nossa Política de
                                    Privacidade e pelas leis de privacidade
                                    aplicáveis. Por exemplo, trabalhamos com as
                                    Escolas para proteger os Dados Pessoais do
                                    Estudante de maneira consistente com a
                                    FERPA. Para Usuários Escolares menores de 13
                                    anos, nossas práticas de privacidade são
                                    informadas pelo Children's Online Privacy
                                    Protection Act ("<strong>COPPA</strong>").
                                    Se você tiver alguma dúvida sobre revisar,
                                    modificar ou excluir as informações pessoais
                                    de um Usuário Escolar, entre em contato
                                    diretamente com sua Escola.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    <strong>
                                        Criação de Contas Escolares:
                                    </strong>
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Observe que, para que as contas de Usuários
                                    Escolares sejam associadas a uma Escola e
                                    reconhecidas como Contas Escolares (conforme
                                    definido em nossos{" "}
                                    <Link
                                        target="_blank"
                                        inline={true}
                                        href="/about/docs/khan-academy-terms-of-service"
                                    >
                                        Termos de Serviço
                                    </Link>
                                    ), as contas devem ser criadas por ou sob a
                                    direção de uma Escola, usando um endereço de
                                    e-mail escolar e associadas a uma turma da
                                    Escola no Serviço.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Por exemplo:
                                </p>
                                <ul className={css(styles.list)}>
                                    <li>
                                        Um professor cria o nome de usuário,
                                        login e senha para estabelecer as contas
                                        de Usuários Escolares e cria uma turma
                                        no Serviço.
                                    </li>
                                    <li>
                                        Um professor inscreve uma turma usando o
                                        Google Classroom, Clever ou um serviço
                                        de logon único semelhante para uso
                                        Escolar.
                                    </li>
                                    <li>
                                        Uma conta é criada por um Usuário
                                        Escolar sob a direção de uma Escola,
                                        usando um endereço de e-mail da Escola e
                                        associada a uma turma da Escola no
                                        Serviço.
                                    </li>
                                    <li>
                                        Contas são criadas conforme um contrato
                                        separado entre a Khan Academy e o
                                        distrito escolar (ou agência
                                        educacional) especificando que as contas
                                        são Contas Escolares.
                                    </li>
                                </ul>
                                <p className={css(styles.paragraph)}>
                                    Nos casos em que não temos um contrato
                                    separado com um distrito escolar ou agência
                                    educacional, podemos exigir a notificação de
                                    que as contas são usadas para fins escolares
                                    para reconhecê-las como Contas Escolares. Se
                                    você é uma Escola, entre em contato com
                                    nossa{" "}
                                    <Link
                                        target="_blank"
                                        inline={true}
                                        href="mailto:schoolpartnerships@khanacademy.org"
                                    >
                                        Equipe de Parcerias Escolares
                                    </Link>{" "}
                                    para saber mais sobre como garantir que as
                                    contas de estudantes sejam configuradas e
                                    gerenciadas como Contas Escolares. Contas
                                    incluídas em uma assinatura do Khan Academy
                                    Districts são Contas Escolares.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Consulte "Saiba Mais" para ver mais
                                    informações sobre:
                                </p>
                                <ul className={css(styles.list)}>
                                    <li>
                                        Algumas maneiras específicas de como
                                        usamos e protegemos Dados Pessoais do
                                        Estudante.
                                    </li>
                                    <li>
                                        Como divulgamos Dados Pessoais do
                                        Estudante.
                                    </li>
                                    <li>Nenhuma Publicidade Direcionada.</li>
                                    <li>
                                        Como retemos e excluímos Dados Pessoais
                                        do Estudante.
                                    </li>
                                    <li>
                                        Perguntas sobre Dados Pessoais do
                                        Estudante.
                                    </li>
                                </ul>
                                {this.renderLearnMore(
                                    7,
                                    <>
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Algumas maneiras específicas
                                                como usamos e protegemos Dados
                                                Pessoais do Estudante:
                                            </strong>
                                        </h4>
                                        <ul className={css(styles.list)}>
                                            <li>
                                                Coletamos, mantemos, usamos e
                                                compartilhamos Dados Pessoais do
                                                Estudante apenas para fins
                                                educacionais autorizados e
                                                conforme descrito em nossa
                                                Política de Privacidade,
                                                inclusive conforme orientado
                                                pela Escola, pelo Usuário
                                                Escolar e/ou pelo pai ou
                                                responsável legal do aluno (um "
                                                <strong>Pai</strong>
                                                ").
                                            </li>
                                            <li>
                                                Não divulgamos Dados Pessoais de
                                                Estudantes para fins de
                                                publicidade direcionada. Tomamos
                                                medidas para evitar o uso de
                                                cookies de rastreamento em
                                                páginas com conteúdo direcionado
                                                a crianças.
                                            </li>
                                            <li>
                                                Não construímos um perfil
                                                pessoal de um Usuário Escolar,
                                                exceto para fins educacionais ou
                                                conforme autorizado por um Pai.
                                            </li>
                                            <li>
                                                Implementamos e mantemos um
                                                programa de segurança de dados
                                                projetado para proteger os tipos
                                                de Dados Pessoais do Estudante
                                                coletados e usados pelo Serviço.
                                            </li>
                                            <li>
                                                Divulgaremos claramente e de
                                                forma transparente nossas
                                                políticas e práticas de dados
                                                para nossos usuários.
                                            </li>
                                            <li>
                                                Nunca venderemos Dados Pessoais
                                                de Estudantes. Se entrarmos em
                                                uma transação corporativa, como
                                                uma fusão, aquisição, falência
                                                ou outra venda de ativos,
                                                resultando em uma transferência
                                                de contas de usuário, exigiremos
                                                que o novo proprietário continue
                                                a honrar os termos estabelecidos
                                                nesta Política de Privacidade ou
                                                forneceremos à Escola um aviso e
                                                uma oportunidade para optar pela
                                                não transferência dos Dados
                                                Pessoais dos Estudantes,
                                                excluindo as contas ou os Dados
                                                Pessoais dos Estudantes antes da
                                                transferência ocorrer. Não
                                                faremos alterações materiais em
                                                nossa Política de Privacidade ou
                                                acordos contratuais relacionados
                                                à coleta ou uso de Dados
                                                Pessoais do Estudante sem antes
                                                notificar a Escola e fornecer
                                                uma escolha antes que os Dados
                                                Pessoais do Estudante sejam
                                                usados de maneira
                                                substancialmente diferente
                                                daquela divulgada quando as
                                                informações foram coletadas.
                                            </li>
                                        </ul>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Como divulgamos Dados Pessoais
                                                do Estudante:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Divulgamos Dados Pessoais do
                                            Estudante da maneira descrita em
                                            nossa Política de Privacidade na
                                            seção intitulada{" "}
                                            <Link inline={true} href="#sharing">
                                                Divulgação de informações
                                            </Link>
                                            . Além disso, dependendo dos
                                            recursos e controles de conta
                                            aplicáveis às Contas de Escola,
                                            podemos divulgar informações a
                                            outros usuários no Serviço, como
                                            professores, treinadores ou
                                            administradores escolares. Se um
                                            Usuário Escolar postar conteúdo em
                                            um fórum da comunidade, seu nome de
                                            usuário e informações de perfil
                                            serão visíveis junto com o
                                            comentário.
                                        </p>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            Dependendo da forma como a Khan
                                            Academy é usada por uma Escola e dos
                                            termos do acordo entre a Escola e a
                                            Khan Academy, a Khan Academy pode
                                            fornecer à Escola acesso a
                                            determinados Dados Pessoais do
                                            Estudante, informações de uso da
                                            Conta Escolar e informações de uso
                                            da conta do professor para fins
                                            escolares, como monitorar a
                                            atividade de aprendizado do aluno e
                                            avaliar a eficácia do uso do Serviço
                                            pela Escola. A Khan Academy também
                                            pode fornecer às Escolas acesso a
                                            Dados Pessoais do Estudante,
                                            informações de uso do Estudante e
                                            informações de uso do professor em
                                            resposta a uma solicitação da
                                            Escola.
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Sem Publicidade Direcionada:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            A Khan Academy não utiliza, divulga
                                            ou compila Dados Pessoais do
                                            Estudante Usuário Escolar no Serviço
                                            com o propósito de marketing ou
                                            publicidade de produtos ou serviços
                                            comerciais, incluindo publicidade
                                            direcionada.
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Como Retemos e Deletamos Dados
                                                Pessoais do Estudante:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Não manteremos conscientemente Dados
                                            Pessoais do Estudante além do
                                            período necessário para apoiar um
                                            propósito educacional, a menos que
                                            autorizado por uma Escola, estudante
                                            ou Pai. Muitos de nossos usuários
                                            continuam a usar suas contas para
                                            fins pessoais, não escolares, e
                                            optam por associar suas contas a uma
                                            conta pessoal ou à conta de seus
                                            Pais. Nesses casos, o usuário (ou o
                                            Pai do usuário) é responsável pelas
                                            solicitações de exclusão da conta.
                                        </p>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            Usuários com contas pessoais (ou
                                            seus Pais) podem excluir suas contas
                                            e informações pessoais associadas à
                                            conta a qualquer momento por meio
                                            das Configurações da conta. Consulte
                                            "
                                            <Link
                                                target="_blank"
                                                inline={true}
                                                href="https://khanacademy.zendesk.com/hc/articles/202263014-How-can-I-cancel-or-delete-my-account-"
                                            >
                                                Como posso cancelar ou excluir
                                                minha conta
                                            </Link>
                                            " no Centro de Ajuda da Khan Academy
                                            para obter instruções.
                                        </p>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            Dependendo da forma como a Khan
                                            Academy é usada por uma Escola e dos
                                            termos de um acordo entre a Escola e
                                            a Khan Academy, os usuários,
                                            incluindo Usuários Escolares (ou
                                            seus Pais), podem ser capazes de
                                            excluir suas contas e informações
                                            pessoais associadas à conta por meio
                                            das Configurações da conta. Consulte
                                            "
                                            <Link
                                                target="_blank"
                                                inline={true}
                                                href="https://khanacademy.zendesk.com/hc/articles/202263014-How-can-I-cancel-or-delete-my-account-"
                                            >
                                                Como posso cancelar ou excluir
                                                minha conta
                                            </Link>
                                            " no Centro de Ajuda da Khan Academy
                                            para obter instruções. Em alguns
                                            casos (por exemplo, nosso serviço
                                            Khan Academy Districts), nossos
                                            parceiros escolares implementaram a
                                            Khan Academy por meio de um acordo
                                            entre a Khan Academy e o distrito
                                            escolar ou agência educacional que
                                            inclui serviços adicionais de
                                            cadastro e administração; nesses
                                            casos, os Usuários Escolares (ou
                                            seus Pais) podem não conseguir
                                            excluir contas por meio das
                                            Configurações da conta, e as
                                            solicitações de exclusão podem
                                            precisar ser encaminhadas à Escola.
                                        </p>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            A Escola é responsável por gerenciar
                                            Dados Pessoais do Estudante em
                                            contas que a Escola não precisa mais
                                            para um propósito educacional,
                                            enviando uma solicitação de
                                            exclusão. As Escolas devem entrar em
                                            contato com nossa{" "}
                                            <Link
                                                target="_blank"
                                                inline={true}
                                                href="mailto:schoolpartnerships@khanacademy.org"
                                            >
                                                Equipe de Parcerias Escolares
                                            </Link>{" "}
                                            para solicitar a exclusão de Dados
                                            Pessoais do Estudante associados a
                                            Contas Escolares. Podemos solicitar
                                            mais informações de você para
                                            verificar a autenticidade de uma
                                            solicitação de exclusão. Se um
                                            Usuário Escolar tiver associado sua
                                            Conta Escolar a uma conta pessoal, a
                                            atividade de aprendizado do Usuário
                                            Escolar pode ser retida na conta
                                            pessoal.
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Perguntas sobre Dados Pessoais
                                                do Estudante:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Se você tiver dúvidas sobre práticas
                                            específicas relacionadas a Dados
                                            Pessoais do Estudante fornecidos à
                                            Khan Academy por uma Escola,
                                            encaminhe suas perguntas à sua
                                            Escola.
                                        </p>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            Esta seção não se aplica a
                                            informações que um estudante ou
                                            outra pessoa pode fornecer à Khan
                                            Academy independentemente do uso do
                                            estudante do Serviço sob a direção
                                            da Escola. Por exemplo, se um
                                            usuário vincular sua conta pessoal
                                            existente à sua Conta Escolar ou
                                            optar por reter sua atividade de
                                            aprendizado da Khan Academy em uma
                                            conta pessoal, as informações
                                            associadas à conta pessoal não são
                                            consideradas Dados Pessoais do
                                            Estudante.
                                        </p>
                                    </>,
                                )}

                                <h2
                                    id="childrens-privacy"
                                    className={css(styles.sectionTitle)}
                                >
                                    Aviso de Privacidade para Crianças:
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    Este aviso de privacidade complementa nossa
                                    Política de Privacidade e fornece
                                    informações adicionais sobre como coletamos,
                                    usamos e divulgamos informações pessoais de
                                    crianças menores de 13 anos ("
                                    <strong>Criança</strong>" ou "
                                    <strong>Crianças</strong>").
                                </p>
                                <p className={css(styles.paragraph)}>
                                    <strong>
                                        Compromisso da Khan Academy com a
                                        Privacidade das Crianças:
                                    </strong>
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Proteger a privacidade das crianças é
                                    especialmente importante para a Khan
                                    Academy. Por esse motivo, criamos certos
                                    recursos projetados para ajudar a proteger
                                    informações pessoais relacionadas a crianças
                                    ("<strong>Usuários Infantis</strong>").
                                    Quando uma criança cria uma conta, coletamos
                                    suas informações em conformidade com as leis
                                    aplicáveis, inclusive buscando o
                                    consentimento de um pai ou responsável legal
                                    ("<strong>Pai</strong>") para a criação
                                    dessa conta. Quando a Khan Academy é usada
                                    por uma Escola em um ambiente educacional,
                                    dependemos da Escola para fornecer o
                                    consentimento necessário, em nome do Pai,
                                    para que a Khan Academy colete informações
                                    de um Usuário Escolar com menos de 13 anos
                                    para fins educacionais.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Consulte "Saiba mais" para obter mais
                                    informações sobre:
                                </p>
                                <ul className={css(styles.list)}>
                                    <li>
                                        Como as crianças podem usar e se
                                        registrar em nosso Serviço
                                    </li>
                                    <li>
                                        Restrições aplicadas a Contas para
                                        Usuários Infantis
                                    </li>
                                    <li>
                                        Informações coletadas e como as
                                        informações são usadas
                                    </li>
                                    <li>Informações divulgadas</li>
                                    <li>
                                        Sem Rastreamento de Terceiros e Sem
                                        Publicidade Direcionada
                                    </li>
                                    <li>
                                        Escolha: Acesso, Modificação e Exclusão
                                        de Contas Infantis
                                    </li>
                                </ul>

                                {this.renderLearnMore(
                                    8,
                                    <>
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Como as Crianças Podem Usar e se
                                                Registrar em Nosso Serviço:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Os usuários podem visualizar nosso
                                            conteúdo e fazer exercícios sem
                                            criar uma conta. A funcionalidade é
                                            limitada se os usuários não criarem
                                            uma conta.
                                        </p>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            A Khan Academy não permite
                                            conscientemente que Usuários
                                            Infantis criem uma conta sem o
                                            consentimento de um Pai ou da
                                            Escola. Se a Khan Academy tomar
                                            conhecimento de que informações
                                            pessoais de um Usuário Infantil
                                            foram coletadas em nosso Serviço sem
                                            o consentimento de um Pai ou da
                                            Escola, a Khan Academy tomará as
                                            medidas apropriadas para excluir
                                            essas informações. Se você é um Pai
                                            e descobriu que seu filho com menos
                                            de 13 anos registrou uma conta
                                            pessoal em nosso Serviço sem
                                            consentimento,{" "}
                                            <Link inline={true} href="#contact">
                                                entre em contato com a Khan
                                                Academy
                                            </Link>{" "}
                                            e solicite que excluamos as
                                            informações pessoais da criança de
                                            nossos sistemas.
                                        </p>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            Um Usuário Infantil pode se
                                            inscrever em uma Conta da Khan
                                            Academy de uma das seguintes
                                            maneiras:
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Um Usuário Infantil pode se
                                                inscrever em uma Conta da Khan
                                                Academy com o consentimento dos
                                                pais:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Quando um Usuário Infantil se
                                            registra em nosso Serviço,
                                            solicitamos um nome de usuário, data
                                            de nascimento, senha e o endereço de
                                            e-mail de um dos pais, para que
                                            possamos enviar um e-mail ao Pai a
                                            fim de buscar o consentimento para
                                            que a Criança use o Serviço. A Khan
                                            Academy não solicita ao Usuário
                                            Infantil mais informações do que o
                                            necessário para fornecer o Serviço
                                            ao Usuário Infantil ou buscar o
                                            consentimento dos Pais. O Usuário
                                            Infantil pode usar o Serviço por 7
                                            dias por meio de uma{" "}
                                            <Link
                                                inline={true}
                                                href="#restricted"
                                                style={{
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                Conta Restrita
                                            </Link>{" "}
                                            enquanto notificamos os Pais e
                                            solicitamos consentimento. Se não
                                            recebermos o consentimento dos Pais
                                            dentro de 7 dias, a Conta Restrita
                                            do Usuário Infantil é fechada, e as
                                            informações pessoais da Criança são
                                            excluídas de nossos sistemas. A Khan
                                            Academy pode contar com uma Escola
                                            para fornecer consentimento em nome
                                            de um Pai, conforme descrito abaixo.
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Quando uma conta de Usuário
                                                Infantil é criada por uma
                                                Escola, contamos com o
                                                consentimento fornecido pela
                                                Escola:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Quando o Serviço da Khan Academy é
                                            usado por uma Escola em um ambiente
                                            educacional, permitimos que a Escola
                                            crie contas de Usuários Infantis e
                                            forneça o consentimento necessário,
                                            em nome dos Pais do Usuário
                                            Infantil, para que a Khan Academy
                                            colete informações pessoais de um
                                            Usuário Infantil para esse fim. As
                                            Escolas podem criar uma conta de
                                            Usuário Infantil adicionando alunos
                                            ao Serviço por meio do Google
                                            Classroom, Clever, ou serviço de
                                            login único semelhante, fornecendo
                                            um link ou código de classe para
                                            participar de uma aula, ou a Escola
                                            pode criar logins e senhas para cada
                                            aluno individualmente. Quando as
                                            Escolas criam contas dessa maneira,
                                            contamos com o consentimento
                                            fornecido pela Escola e não exigimos
                                            consentimento adicional dos Pais.
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Métodos para Fornecer
                                                Consentimento dos Pais:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Os Pais podem fornecer consentimento
                                            para que um Usuário Infantil use o
                                            Serviço por meio de uma Conta
                                            Restrita, respondendo
                                            afirmativamente a um e-mail enviado
                                            pela Khan Academy para o endereço de
                                            e-mail dos Pais fornecido pelo
                                            Usuário Infantil durante o registro.
                                            Se não recebermos o consentimento
                                            dos Pais (ou confirmação de que a
                                            conta é uma Conta Escolar) dentro de
                                            7 dias, a conta do Usuário Infantil
                                            será fechada, e as informações
                                            pessoais da Criança serão excluídas
                                            de nossos sistemas. Contas Restritas
                                            podem acessar e usar determinados
                                            recursos do Serviço de maneira
                                            limitada.
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>Contas dos Pais:</strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Para habilitar funcionalidades
                                            adicionais para a conta do Usuário
                                            Infantil e ter controle total sobre
                                            a atividade da conta do Usuário
                                            Infantil, o Pai deve criar uma
                                            "Conta dos Pais" no Serviço e se
                                            conectar à conta do Usuário Infantil
                                            clicando no link de criação de conta
                                            no aviso de consentimento dos Pais.
                                            As Contas dos Pais controlam as
                                            configurações e funcionalidades da
                                            conta do Usuário Infantil. O Pai
                                            pode manter a Conta Restrita da
                                            Criança e habilitar funcionalidades
                                            e recursos adicionais da conta, se
                                            desejar. Os Pais podem revogar as
                                            permissões de um Usuário Infantil
                                            para usar o Serviço a qualquer
                                            momento excluindo a conta. Em alguns
                                            casos, controles administrativos,
                                            incluindo a capacidade de modificar
                                            ou excluir a conta, são de
                                            responsabilidade da Escola.
                                        </p>
                                        <br />
                                        <h4
                                            id="restricted"
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Contas Restritas para Usuários
                                                Infantis:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            A Khan Academy tenta restringir o
                                            acesso de um Usuário Infantil a
                                            certos recursos que poderiam
                                            resultar na divulgação das
                                            informações pessoais da Criança. Uma
                                            Conta Restrita limita o
                                            compartilhamento e a exibição de
                                            informações privadas sobre o Usuário
                                            Infantil de várias maneiras.
                                        </p>
                                        <p className={css(styles.paragraph)}>
                                            Por exemplo, um Usuário Infantil com
                                            uma Conta Restrita não pode:
                                        </p>
                                        <ul className={css(styles.list)}>
                                            <li>
                                                Exibir informações pessoais além
                                                de um nome de usuário. As Contas
                                                Restritas contêm apenas o nome
                                                de usuário da Criança e não
                                                incluem outras informações
                                                pessoais ou informações de
                                                perfil. Como o nome de usuário
                                                pode ser exibido no Serviço,
                                                recomendamos fortemente
                                                selecionar um nome de usuário
                                                que não esteja relacionado ou
                                                identifique o nome real da
                                                Criança. O nome de usuário da
                                                Criança pode ser selecionado ou
                                                revisado pelo Pai ao criar uma
                                                Conta dos Pais.
                                            </li>
                                            <li>
                                                Adicionar ou editar informações
                                                pessoais associadas a uma Conta
                                                Restrita.
                                            </li>
                                            <li>
                                                Publicar em fóruns públicos da
                                                comunidade ou fazer perguntas ou
                                                respostas em lições.
                                            </li>
                                            <li>
                                                Receber e-mails da Khan Academy
                                                (todos os e-mails são enviados
                                                para o e-mail dos Pais no
                                                arquivo).
                                            </li>
                                            <li>
                                                Comunicar-se ou divulgar
                                                informações pessoais a
                                                Treinadores, exceto um nome de
                                                usuário e atividade de uso do
                                                Serviço (ou seja, lições
                                                concluídas, distintivos
                                                conquistados, atividade de
                                                aprendizado, interações com o
                                                Khanmigo etc.). Da mesma forma,
                                                Treinadores não podem se
                                                comunicar com um Usuário
                                                Infantil com uma Conta Restrita,
                                                exceto para atribuir conteúdo ao
                                                Usuário Infantil.
                                            </li>
                                        </ul>
                                        <br />
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Quais informações coletamos de
                                                um Usuário Infantil e como
                                                usamos essas informações:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Coletamos um nome de usuário, data
                                            de nascimento e endereço de e-mail
                                            dos Pais quando um Usuário Infantil
                                            se registra no Serviço, bem como
                                            qualquer outra informação pessoal
                                            que um Pai adicione à conta do
                                            Usuário Infantil, conforme descrito
                                            acima. Coletamos informações sobre o
                                            uso do Serviço pelo Usuário Infantil
                                            (lições concluídas, distintivos
                                            conquistados). Também coletamos
                                            informações de uso e do dispositivo,
                                            conforme descrito em{" "}
                                            <Link
                                                inline={true}
                                                href="#collection-info"
                                            >
                                                Coleta de informações
                                            </Link>
                                            .
                                        </p>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            Usamos as informações coletadas
                                            sobre os Usuários Infantis para
                                            fornecer os Serviços, personalizar
                                            sua experiência com conteúdo
                                            apropriado e envolvente,
                                            comunicar-nos com eles, melhorar
                                            nossos serviços, desenvolver novas
                                            ofertas e para proteger a segurança
                                            de nossos usuários e cumprir
                                            requisitos legais. Para obter mais
                                            informações sobre como usamos
                                            informações sobre Usuários Infantis,
                                            consulte nossa descrição sobre{" "}
                                            <Link
                                                inline={true}
                                                href="#use-of-info"
                                            >
                                                Uso de informações
                                            </Link>
                                            . Usamos o endereço de e-mail dos
                                            Pais para enviar mensagens sobre a
                                            conta.
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Como divulgamos informações
                                                relacionadas a um Usuário
                                                Infantil:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Como descrito anteriormente, as
                                            Contas Restritas têm capacidades
                                            limitadas de compartilhamento de
                                            informações. Um nome de usuário e
                                            informações de progresso, juntamente
                                            com qualquer informação pessoal
                                            adicionada pelo Pai ao perfil da
                                            Criança, são divulgados a um Coach
                                            ou professor adicionado à conta do
                                            Usuário Infantil, embora uma Conta
                                            de Pai possa restringir o Usuário
                                            Infantil de adicionar Coaches à
                                            conta. Os Pais também podem ativar
                                            recursos adicionais de
                                            compartilhamento de informações por
                                            meio de uma Conta de Pai, por
                                            exemplo, adicionando informações de
                                            perfil e permitindo que o perfil
                                            seja divulgado a Coaches e outros
                                            usuários, ou permitindo que o
                                            Usuário Infantil publique conteúdo
                                            (por exemplo, perguntas e respostas)
                                            no Serviço. Pais que ativam recursos
                                            de postagem de conteúdo devem estar
                                            cientes de que um Usuário Infantil
                                            pode postar informações pessoais
                                            identificáveis em conteúdo livre no
                                            Serviço e devem monitorar as
                                            postagens do Usuário Infantil e
                                            excluir informações pessoais, se
                                            necessário. Também podemos divulgar
                                            informações pessoais de Usuários
                                            Infantis por razões legais, de
                                            conformidade ou de segurança,
                                            conforme descrito em{" "}
                                            <Link inline={true} href="#sharing">
                                                Divulgação de informações
                                            </Link>
                                            .
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Sem Rastreamento por Terceiros e
                                                Sem Publicidade Direcionada
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            A Khan Academy não exibe publicidade
                                            direcionada no Serviço. Não
                                            divulgamos informações pessoais de
                                            Usuários Infantis para fins de
                                            marketing direto ou para fins de
                                            publicidade direcionada. Além disso,
                                            tomamos medidas para desativar redes
                                            de anúncios de terceiros em páginas
                                            da web com conteúdo direcionado a
                                            crianças.
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Como acessar, modificar e
                                                excluir contas de crianças:
                                            </strong>
                                        </h4>
                                        <p className={css(styles.paragraph)}>
                                            Como Pai, você tem a capacidade de
                                            acessar e controlar informações
                                            sobre seu Usuário Infantil na seção
                                            "Seus Filhos" de sua Conta de Pai.
                                            Para recusar a coleta ou uso futuro
                                            das informações pessoais de seu
                                            filho, você pode excluir a Conta do
                                            seu Usuário Infantil seguindo as
                                            instruções em nossas{" "}
                                            <Link
                                                target="_blank"
                                                inline={true}
                                                href="https://khanacademy.zendesk.com/hc/articles/202263014"
                                            >
                                                Perguntas Frequentes
                                            </Link>
                                            . Observe que você deve excluir a
                                            conta do seu Usuário Infantil antes
                                            de excluir a Conta de Pai.
                                        </p>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            Se você não tiver uma Conta de Pai
                                            associada à Conta Restrita do seu
                                            Usuário Infantil, pode entrar em
                                            contato conosco em nossa{" "}
                                            <Link
                                                target="_blank"
                                                inline={true}
                                                href="https://support.khanacademy.org/hc/requests/new"
                                            >
                                                página de Suporte
                                            </Link>{" "}
                                            para acessar ou excluir a conta do
                                            Usuário Infantil. Observe que você
                                            precisará do nome de usuário do
                                            Usuário Infantil e podemos tomar
                                            medidas para autenticar sua
                                            identidade antes de fornecer acesso
                                            à Conta Restrita.
                                        </p>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            Em alguns casos, controles
                                            administrativos, incluindo a
                                            capacidade de modificar ou excluir a
                                            conta, são mantidos pela Escola, e
                                            você precisará encaminhar sua
                                            solicitação à Escola. Para obter
                                            mais informações, consulte{" "}
                                            <Link inline={true} href="#schools">
                                                Escolas e Uso de Estudantes
                                            </Link>
                                            .
                                        </p>
                                    </>,
                                )}

                                <h2
                                    id="donor-info"
                                    className={css(styles.sectionTitle)}
                                >
                                    Informações sobre Doadores:
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    Esta notificação de Informações sobre
                                    Doadores complementa nossa Política de
                                    Privacidade em relação aos doadores. Se você
                                    doar para a Khan Academy e não doar
                                    anonimamente, obtemos informações como seu
                                    nome, endereço de e-mail e endereço
                                    residencial. Se você doar usando nosso
                                    formulário online, nosso processador de
                                    pagamento também coletará seu número de
                                    cartão de crédito ou débito ou outras
                                    informações de pagamento. Observe que as
                                    informações coletadas por esses
                                    processadores de pagamento estão sujeitas à
                                    sua própria política de privacidade.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Podemos usar as informações do doador para
                                    nos comunicarmos com você sobre sua
                                    contribuição e enviar atualizações de
                                    captação de recursos e informações sobre o
                                    avanço de nossa missão.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Não divulgamos informações pessoalmente
                                    identificáveis de doadores, exceto com o seu
                                    consentimento ou conforme exigido por lei.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Se você se registrou para uma conta conosco,
                                    poderá atualizar suas preferências nas
                                    configurações da sua conta para cancelar a
                                    assinatura de e-mails de doadores. Se você
                                    não tem uma conta conosco, pode cancelar a
                                    assinatura clicando no link de cancelamento
                                    de inscrição na mensagem de e-mail do doador
                                    ou respondendo ao e-mail pedindo para ser
                                    removido. Observe que, se você tiver
                                    cancelado a inscrição anteriormente, mas
                                    posteriormente fizer uma doação (ou
                                    participar de um evento para doadores),
                                    poderemos enviar a você comunicações de
                                    acompanhamento relacionadas ao seu apoio à
                                    nossa missão.
                                </p>

                                {this.renderLearnMore(
                                    9,
                                    <>
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Nossos Compromissos:
                                            </strong>
                                        </h4>
                                        <ul className={css(styles.list)}>
                                            <li>
                                                Não venderemos, compartilharemos
                                                ou negociaremos os nomes ou
                                                informações pessoais de nossos
                                                doadores com qualquer outra
                                                entidade.
                                            </li>
                                            <li>
                                                Não enviaremos correspondências
                                                a nossos doadores em nome de
                                                outras organizações.
                                            </li>
                                            <li>
                                                Coletaremos informações de
                                                pagamento, endereço de cobrança
                                                e outras informações necessárias
                                                para processar uma doação ou
                                                registro de evento.
                                            </li>
                                            <li>
                                                Usaremos suas informações para
                                                cumprir a lei ou na crença
                                                sincera de que tal ação é
                                                necessária para se conformar aos
                                                requisitos legais ou cumprir um
                                                processo legal que nos foi
                                                entregue.
                                            </li>
                                            <li>
                                                Podemos usar as informações
                                                pessoais para nos proteger
                                                contra possíveis fraudes.
                                                Podemos verificar com terceiros
                                                as informações coletadas no
                                                processo de processamento de uma
                                                doação, registro de evento ou
                                                outra doação.
                                            </li>
                                            <li>
                                                Se você usar um cartão de
                                                crédito ou débito no site,
                                                podemos usar serviços de
                                                autorização de cartão e triagem
                                                de fraudes para verificar se
                                                suas informações de cartão e
                                                endereço coincidem com as
                                                informações fornecidas a nós e
                                                se o cartão sendo usado não foi
                                                relatado como perdido ou
                                                roubado.
                                            </li>
                                        </ul>
                                    </>,
                                )}
                                <h2
                                    id="paid-products"
                                    className={css(styles.sectionTitle)}
                                >
                                    Produtos pagos:
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    Este aviso sobre produtos pagos complementa
                                    nossa Política de Privacidade em relação aos
                                    produtos pagos. Se você adquirir uma
                                    assinatura paga de um serviço da Khan
                                    Academy, como o Khanmigo, obteremos
                                    informações como seu nome, endereço de
                                    e-mail e endereço postal, e nosso
                                    processador de pagamentos também coletará o
                                    número do seu cartão de crédito ou débito ou
                                    outras informações de pagamento. Observe que
                                    as informações coletadas por esses
                                    processadores de pagamento estão sujeitas à
                                    política de privacidade deles.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Podemos usar essas informações para
                                    gerenciar os serviços que você recebe e para
                                    nos comunicar com você sobre sua assinatura.
                                </p>
                                <h2
                                    id="applicant"
                                    className={css(styles.sectionTitle)}
                                >
                                    Informações do Candidato:
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    Este aviso de Informações do Candidato
                                    complementa nossa Política de Privacidade em
                                    relação aos candidatos. Se você se
                                    candidatar a uma vaga no Khan Academy por
                                    meio de um aplicativo online ou site de
                                    recrutamento, obteremos as informações que
                                    você optar por fornecer em sua candidatura
                                    para esse fim. Por exemplo, coletamos
                                    informações profissionais ou relacionadas ao
                                    emprego que geralmente incluem detalhes
                                    sobre seu histórico profissional,
                                    experiência e qualificações; bem como
                                    informações educacionais, incluindo diplomas
                                    obtidos e registros acadêmicos.
                                </p>
                                <h2
                                    id="regional-info"
                                    className={css(styles.sectionTitle)}
                                >
                                    Informações Regionais:
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    Este aviso de Informações Regionais
                                    complementa nossa Política de Privacidade.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    As práticas de privacidade da Khan Academy
                                    são projetadas para apoiar os direitos e
                                    expectativas de privacidade dos consumidores
                                    globalmente, incluindo os direitos de
                                    privacidade comuns a muitos países onde
                                    nossos serviços estão disponíveis e que
                                    podem ser aplicáveis a você. Esses direitos
                                    incluem:
                                </p>
                                <ul className={css(styles.list)}>
                                    <li>
                                        O direito de entender quais informações
                                        pessoais coletamos, como as usamos, como
                                        são processadas, armazenadas e
                                        protegidas, com quem são compartilhadas
                                        e se são vendidas.
                                    </li>
                                    <li>
                                        O direito de corrigir suas informações
                                        pessoais.
                                    </li>
                                    <li>
                                        O direito de solicitar que suas
                                        informações pessoais sejam excluídas ou
                                        anonimizada.
                                    </li>
                                    <li>
                                        O direito de se opor ao uso ou
                                        processamento de suas informações
                                        pessoais, incluindo a opção de não
                                        permitir a venda de suas informações
                                        pessoais.
                                    </li>
                                    <li>
                                        O direito de retirar o consentimento
                                        para o processamento de dados.
                                    </li>
                                    <li>
                                        O direito de apresentar uma reclamação à
                                        autoridade supervisora competente; e
                                    </li>
                                    <li>
                                        O direito de ser livre de discriminação
                                        por exercer seus direitos de
                                        privacidade.
                                    </li>
                                </ul>
                                <p className={css(styles.paragraph)}>
                                    Os direitos específicos de privacidade e os
                                    processos para exercê-los variam de país
                                    para país. Em algumas regiões, você pode ter
                                    direitos adicionais. A descrição dos
                                    direitos e os processos e procedimentos a
                                    serem seguidos para exercê-los podem variar,
                                    e em alguns casos, os direitos devem ser
                                    processados de acordo com prazos
                                    específicos. Podemos exigir que você, ou seu
                                    representante legal, comprove adequadamente
                                    sua identidade antes de tomarmos qualquer
                                    ação em sua solicitação. É imperativo que
                                    você forneça todas as informações
                                    solicitadas de maneira rápida (conforme
                                    orientado pela sua regulamentação local)
                                    para cumprir os requisitos aplicáveis.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Se desejar exercer um direito de
                                    privacidade, por favor,{" "}
                                    <Link inline={true} href="#contact">
                                        entre em contato com a Khan Academy
                                    </Link>
                                    . Nossa capacidade de responder a
                                    solicitações específicas pode ser limitada
                                    pela legislação aplicável e pela
                                    funcionalidade de nosso serviço. Por
                                    exemplo, podemos precisar reter alguns dados
                                    por motivos comerciais legítimos ou porque o
                                    processamento é necessário para fornecer os
                                    serviços que você solicitou.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Podemos fornecer informações complementares
                                    em relação à prestação de nossos serviços em
                                    determinadas regiões ou em conexão com
                                    programas específicos que oferecemos fora
                                    dos Estados Unidos, incluindo no{" "}
                                    <Link
                                        inline={true}
                                        target="_blank"
                                        href="https://khanacademy.zendesk.com/hc/"
                                    >
                                        Centro de Ajuda da Khan Academy
                                    </Link>{" "}
                                    ou em sites regionais.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    <strong>
                                        Transferência Internacional de Dados:
                                    </strong>{" "}
                                    Nosso Serviço é operado e gerenciado em
                                    servidores localizados nos Estados Unidos.
                                    Se você optar por usar nosso Serviço na
                                    Europa ou em outras regiões do mundo com
                                    leis que regem a coleta e o uso de dados
                                    diferentes das leis dos Estados Unidos, você
                                    reconhece e concorda que o Khan Academy
                                    transferirá, armazenará e processará suas
                                    informações pessoais nos Estados Unidos com
                                    o objetivo de realizar o Serviço de acordo
                                    com nosso contrato (por exemplo, nossos{" "}
                                    <Link
                                        target="_blank"
                                        inline={true}
                                        href="/about/docs/khan-academy-terms-of-service"
                                    >
                                        Termos de Serviço
                                    </Link>
                                    ) e para qualquer outro fim para o qual você
                                    forneça consentimento explícito e informado.
                                </p>

                                {this.renderLearnMore(
                                    10,
                                    <>
                                        <p className={css(styles.paragraph)}>
                                            <strong>
                                                Direitos dos Menores na
                                                Califórnia:
                                            </strong>
                                        </p>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            Se você é um usuário registrado com
                                            menos de 18 anos e reside na
                                            Califórnia, tem o direito de
                                            solicitar a remoção de conteúdo ou
                                            informações que você tenha postado
                                            publicamente em nosso Serviço. Para
                                            solicitar a remoção, entre em{" "}
                                            <Link inline={true} href="#contact">
                                                contato com o Khan Academy
                                            </Link>{" "}
                                            para obter assistência. Observe que
                                            sua solicitação não garante remoção
                                            completa ou abrangente, pois o
                                            conteúdo ou informações podem ser
                                            retidos de acordo com a legislação
                                            aplicável.
                                        </p>
                                        <br />
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            <strong>
                                                Regulamento Geral de Proteção de
                                                Dados (“GDPR”):
                                            </strong>
                                        </h4>
                                        <h4
                                            className={css(
                                                styles.learnMoreSubheading,
                                            )}
                                        >
                                            Se você é residente na União
                                            Europeia, Reino Unido ou em outros
                                            países que fazem parte da Área
                                            Econômica Europeia, você pode ter
                                            certos direitos adicionais:
                                        </h4>
                                        <ul className={css(styles.list)}>
                                            <li>
                                                <strong>
                                                    Direito de Acesso e
                                                    Portabilidade.
                                                </strong>{" "}
                                                Direito de obter acesso às suas
                                                informações pessoais, juntamente
                                                com informações relacionadas, e
                                                recebê-las em um formato
                                                comumente usado e transferi-las
                                                para outro controlador de dados.
                                            </li>
                                            <li>
                                                <strong>
                                                    Direito de Retificação.
                                                </strong>{" "}
                                                Direito de obter retificação de
                                                suas informações pessoais sem
                                                demora indevida, quando essas
                                                informações pessoais estiverem
                                                imprecisas ou incompletas.
                                            </li>
                                            <li>
                                                <strong>
                                                    Direito ao Esquecimento.
                                                </strong>{" "}
                                                Direito de obter o apagamento de
                                                suas informações pessoais sem
                                                demora indevida em certas
                                                circunstâncias, como quando as
                                                informações pessoais não forem
                                                mais necessárias para os fins
                                                para os quais foram coletadas ou
                                                processadas.
                                            </li>
                                            <li>
                                                <strong>
                                                    Direito de Restrição.
                                                </strong>{" "}
                                                Direito de obter a restrição do
                                                processamento de suas
                                                informações pessoais em certas
                                                circunstâncias, como quando a
                                                precisão das informações
                                                pessoais é contestada por você,
                                                por um período que nos permita
                                                verificar a precisão dessas
                                                informações pessoais.
                                            </li>
                                            <li>
                                                <strong>
                                                    Direito de Oposição
                                                </strong>{" "}
                                                Direito de se opor, por motivos
                                                relacionados à sua situação
                                                específica, ao processamento de
                                                suas informações pessoais e de
                                                se opor ao processamento de suas
                                                informações pessoais para fins
                                                de marketing direto, na medida
                                                em que se relaciona a tal
                                                marketing direto.
                                            </li>
                                            <li>
                                                <strong>
                                                    Direito de Retirar o
                                                    Consentimento:
                                                </strong>{" "}
                                                Direito de retirar seu
                                                consentimento a qualquer momento
                                                (embora a retirada do seu
                                                consentimento não afete a
                                                legalidade do processamento que
                                                ocorreu antes de retirar seu
                                                consentimento).
                                            </li>
                                        </ul>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            Você também pode ter o direito de
                                            apresentar uma reclamação à{" "}
                                            <Link
                                                target="_blank"
                                                inline={true}
                                                href="https://edpb.europa.eu/about-edpb/about-edpb/members_en"
                                            >
                                                Autoridade Supervisora relevante
                                            </Link>
                                            . Se você precisar de mais
                                            assistência em relação aos seus
                                            direitos,{" "}
                                            <Link inline={true} href="#contact">
                                                entre em contato conosco
                                            </Link>{" "}
                                            e consideraremos sua solicitação de
                                            acordo com a legislação aplicável.
                                            Em alguns casos, nossa capacidade de
                                            garantir esses direitos para você
                                            pode depender de nossas obrigações
                                            de processar informações pessoais
                                            por motivos de segurança, prevenção
                                            de fraudes, conformidade com
                                            requisitos regulatórios ou legais,
                                            ou porque o processamento é
                                            necessário para fornecer os serviços
                                            que você solicitou. Nesses casos,
                                            informaremos detalhes específicos em
                                            resposta à sua solicitação.
                                        </p>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            Quanto aos dados pessoais sujeitos
                                            ao GDPR, confiamos em várias bases
                                            legais para processar os dados.
                                            Essas bases legais incluem quando:
                                        </p>
                                        <br />
                                        <ul className={css(styles.list)}>
                                            <li>
                                                O processamento é necessário
                                                para cumprir nossas obrigações
                                                contratuais em nossos{" "}
                                                <Link
                                                    target="_blank"
                                                    inline={true}
                                                    href="/about/docs/khan-academy-terms-of-service"
                                                >
                                                    Termos de Serviço
                                                </Link>{" "}
                                                ou outros contratos com você.
                                            </li>
                                            <li>
                                                O processamento é necessário
                                                para os fins de nossos
                                                interesses legítimos, como
                                                melhorar, personalizar e
                                                desenvolver o Serviço, marketing
                                                do Serviço, como novos recursos
                                                ou produtos que possam ser de
                                                interesse, e promover a
                                                segurança, conforme descrito
                                                acima.
                                            </li>
                                            <li>
                                                Você nos dá consentimento para
                                                fazê-lo para um fim específico.
                                            </li>
                                            <li>
                                                O processamento é necessário
                                                para cumprir uma obrigação
                                                legal, uma ordem judicial ou
                                                para exercer ou defender
                                                reivindicações legais.
                                            </li>
                                        </ul>
                                    </>,
                                )}

                                {this.renderLearnMore(
                                    11,
                                    <>
                                        <p className={css(styles.paragraph)}>
                                            <strong>
                                                Disposições específicas para
                                                residentes no Brasil:
                                            </strong>
                                        </p>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            Quando os Serviços são fornecidos e
                                            utilizados na República Federativa
                                            do Brasil (Brasil), o processamento
                                            de dados pessoais estará sujeito e
                                            regido pela Lei Geral de Proteção de
                                            Dados, também conhecida como LGPD
                                            (Lei 13.709/2018). Como residente
                                            brasileiro, você pode ter certos
                                            direitos adicionais em relação aos
                                            seus dados pessoais:
                                        </p>
                                        <br />
                                        <ul className={css(styles.list)}>
                                            <li>
                                                O direito de ser informado sobre
                                                os dados pessoais coletados e de
                                                obter acesso aos seus dados
                                                pessoais.
                                            </li>
                                            <li>
                                                O direito de corrigir dados
                                                incompletos, imprecisos ou
                                                desatualizados.
                                            </li>
                                            <li>
                                                O direito de se opor ao
                                                processamento desnecessário ou
                                                excessivo de seus dados, e de
                                                solicitar que quaisquer dados
                                                pessoais sujeitos a
                                                processamento desnecessário ou
                                                excessivo, em violação à LGPD,
                                                sejam excluídos ou anonimizados.
                                            </li>
                                            <li>
                                                O direito de solicitar uma cópia
                                                dos seus dados pessoais em um
                                                formato portátil.
                                            </li>
                                            <li>
                                                O direito à exclusão de dados
                                                pessoais processados com o seu
                                                consentimento, sujeito a
                                                exceções previstas na LGPD.
                                            </li>
                                            <li>
                                                O direito à informação sobre o
                                                processamento de dados por
                                                terceiros.
                                            </li>
                                            <li>
                                                O direito de retirar o seu
                                                consentimento para o
                                                processamento de seus dados e o
                                                direito de ser informado sobre
                                                as consequências de não dar
                                                consentimento para o
                                                processamento de dados.
                                            </li>
                                        </ul>
                                        <br />
                                        <p className={css(styles.paragraph)}>
                                            Se precisar de assistência adicional
                                            em relação aos seus direitos, entre
                                            em contato com nossa e{" "}
                                            <Link
                                                target="_blank"
                                                inline={true}
                                                href="mailto:privacy@khanacademy.org"
                                            >
                                                Equipe de Privacidade
                                            </Link>{" "}
                                            e consideraremos sua solicitação de
                                            acordo com a legislação aplicável.
                                            Em alguns casos, nossa capacidade de
                                            agir em sua solicitação pode ser
                                            limitada por uma obrigação legal ou
                                            regulatória de reter dados, ou pela
                                            necessidade de reter os dados para
                                            fins de defesa legal. Também podemos
                                            ser limitados ao agir em
                                            determinadas solicitações nos casos
                                            em que o processamento seja
                                            necessário para fornecer os serviços
                                            solicitados por você. Quando esse
                                            for o caso, informaremos detalhes
                                            específicos em resposta à sua
                                            solicitação. Observe que podemos
                                            solicitar confirmação de sua
                                            identidade para processar sua
                                            solicitação.
                                        </p>
                                    </>,
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </PageRoot>
        );
    }
}

const styles = StyleSheet.create({
    wrapper: {
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
    },
    header: {
        display: "flex",
        height: 300,
        width: "100vw",
        backgroundPosition: "center",
        backgroundImage: cssUrl("/images/pp_header_desktop.jpg"),
        alignItems: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        [mediaQueries.smOrSmaller]: {
            height: 0,
            paddingTop: "75%",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            width: "100vw",
            backgroundImage: cssUrl("/images/pp_header_mobile.jpg"),
        },
    },
    headerCopy: {
        display: "flex",
        flexDirection: "column",
        maxWidth: "55%",
        marginLeft: "5%",
        [mediaQueries.smOrSmaller]: {
            marginTop: "-70%",
            maxWidth: "60%",
            marginLeft: "20%",
            justifyContent: "center",
        },
    },
    pageTitle: {
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
        fontSize: 42,
        fontWeight: 600,
        color: color.darkBlue,
        margin: 0,
        [mediaQueries.smOrSmaller]: {
            fontSize: 35,
            textAlign: "center",
        },
    },
    lastUpdated: {
        fontSize: 18,
        fontStyle: "italic",
        fontWeight: 700,
        lineHeight: "30px",
        color: color.darkBlue,
        margin: 0,
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },
    body: {
        flex: 1,
        display: "flex",
        justifyContent: "center",
        padding: "40px 0",
    },
    columns: {
        display: "flex",
        maxWidth: "1200px",
    },
    tableOfContentsColumn: {
        flexDirection: "column",
        fontSize: 18,
        position: "sticky",
        top: 20,
        ...hacks.safariOnly({
            position: "-webkit-sticky",
        }),
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },
    stickyTOC: {
        position: "sticky",
        top: 20,
        marginRight: "8%",
        ...hacks.safariOnly({
            position: "-webkit-sticky",
            alignSelf: "flex-start",
            height: "auto",
        }),
    },
    tocTitle: {
        fontSize: 28,
        lineHeight: "32px",
        color: "#000",
    },
    tableOfContentList: {
        listStyle: "none",
        color: "#1865F2",
        lineHeight: "32px",
        fontSize: 18,
        paddingBottom: 18,
    },
    contentColumn: {
        flex: 2,
        display: "flex",
        flexDirection: "column",
        [mediaQueries.smOrSmaller]: {
            padding: "5%",
        },
    },
    sectionTitle: {
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
        fontSize: 34,
        fontWeight: "normal",
    },
    sectionSubtitle: {
        fontWeight: 700,
        fontSize: 20,
        lineHeight: "32.5px",
    },
    orderedList: {
        listStyleType: "decimal",
        marginLeft: 20,
        lineHeight: "32px",
        fontSize: 18,
        marginBottom: 20,
    },
    list: {
        listStyle: "disc",
        marginLeft: 20,
        lineHeight: "32px",
        fontSize: 18,
        marginBottom: 25,
    },
    subList: {
        listStyle: "circle",
        marginLeft: 25,
        lineHeight: "32px",
        fontSize: 18,
        marginBottom: 0,
    },
    paragraph: {
        fontSize: 18,
        lineHeight: "32px",
        margin: 0,
        marginBottom: 25,
    },
    expandAll: {
        border: 0,
        background: "none",
        textAlign: "right",
        color: "#1865F2",
        float: "right",
        marginBottom: -10,
        cursor: "pointer",
    },
    learnMoreTable: {
        margin: "25px 0",
        width: "100%",
    },
    learnMoreTableContents: {
        backgroundColor: "#F7F8FA",
        transition: `all ${standardTransition}`,
    },
    learnMoreSubheading: {
        fontSize: 20,
        lineHeight: "32px",
    },
    learnMoreTableData: {
        padding: 25,
    },
    learnMoreHeaderContainer: {
        backgroundColor: "#B6D2DA",
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    learnMoreHeader: {
        margin: 0,
        padding: "2%",
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
        fontSize: 24,
        fontWeight: "normal",
    },
});
